import * as THREE from 'three';
import { getStateCallbacks, Room, SeatReservation } from 'colyseus.js';
import type { AuthUser } from '../core/state/auth-store';
import {
  joinDungeonInstance, joinDungeonInstanceViaReservation, sendDoorBar, sendDoorUnbar, sendCrankStart, sendCrankStop,
  sendChestOpen, sendCorpseLoot, sendBrazierDouse, sendBrazierLight, sendExtract, sendLootDrop, sendLootInsure, sendLootUninsure,
  sendAbilitySlot, sendTarget, requestAbilityState, requestLootInventory, sendReviveStart, sendReviveStop, sendWavesLeave,
} from './net/dungeon-instance-client';
import {
  sendMove, sendAim, sendSprint, sendCrouch, sendJump, sendAttack, sendParry, sendAbility, sendFeint, sendBlock,
  sendLanternToggle,
  sendEmote,
} from './net/colyseus-client';
import type { ClientClockSync } from './net/clock-sync';
import { DungeonInstanceScene } from './render/dungeon-instance-scene';
import { CharacterVisual } from './render/character-visual';
import type { AnimIntent } from './render/animation-map';
import { buildInstancedLayout } from './render/instanced-layout';
import { paletteForStratum } from './config/stratum-palette';
import { objectiveFor, gateCompassLabel, gatePromptLines, isFinalFloor } from './ui/run-objective';
import { InteractPrompt, brazierPromptLine, promptLinesForInput, pressKey, holdKey } from './ui/interact-prompt';
import { LootToasts } from './ui/loot-toast';
import { InputController } from './input/input-controller';
import { TouchControls, isTouchDevice } from './input/touch-controls';
import { OverShoulderCameraController, normalizeAngle } from './input/over-shoulder-camera-controller';
import { bindAudioToSettings, bindCameraToSettings, bindGameplayToSettings, gameplayPreferences } from './ui/settings/apply-settings';
import { MouseLookPrompt } from './ui/mouse-look-prompt';
import { EntryVeil } from './ui/entry-veil';
import { LevelUpBanner } from './ui/level-up-banner';
import { DownedOverlay } from './ui/downed-overlay';
import { RunMenu } from './ui/run-menu';
import { AbilityNotice } from './ui/ability-notice';
import { QuestCompleteBanner, type QuestCompleteNotice } from './ui/quest-complete-banner';
import { RemoteInterpolator } from './sim/interpolator';
import { ServerRenderClock } from './sim/render-clock';
import { cameraOcclusionAlpha, localPlayerFadeAlpha } from './render/proximity-fade';
import { CombatHud, type HudPartyMember } from './ui/combat-hud';
import { DamageNumbers, combatTextFor } from './ui/damage-numbers';
import { DebugHud } from './ui/debug-hud';
import { LootPanel } from './ui/loot-panel';
import { DamageFlash } from './ui/damage-flash';
import { RunEndOverlay, type RunEndInfo } from './ui/run-end-overlay';
import { GoalCompass, computeBearing, resolveCompassTarget } from './ui/goal-compass';
import { ActionBar, AbilityStateView } from './ui/action-bar';
import { EmoteMenu } from './ui/emote-menu';
import { FlyThroughOverlay } from './ui/fly-through-overlay';
import { getInsuranceStatus } from './net/insurance-api';
import { getCosmeticCatalog, getMyCosmetics } from './net/cosmetics-api';
import { WORLD_STATE, crankProgressFraction } from './sim/world-state-client';
import { samplePathPosition } from './sim/fly-through-path';
import type { EntityStateName } from './config/tuning';
import { rosterEntry, type CharacterClassId, type WeaponClass } from './config/character-roster';
import { monsterRosterEntry, MONSTER_DISPLAY_NAME, MonsterArchetype } from './config/monster-roster';
import { AudioEngine } from './audio/audio-engine';
import { RemoteFootsteps, FootstepEntity } from './audio/remote-footsteps';
import { AbilityFxLayer, loadAbilityFxSprites } from './render/ability-fx';
import {
  ABILITY_FX_DURATION_MS, abilityVisual, type AbilityFxKind,
} from './config/ability-vfx';
import { FrameTimer } from './sim/frame-timer';
import { LocalJump, JUMP_DURATION_MS, remoteJumpOffset, isFreshJump, isAirborne } from './sim/jump';
import { pickAbilityTarget, pickCycleTarget, pickImplicitTarget, partyKeyTarget, type TargetCandidate } from './sim/targeting';
import { TargetRing } from './render/target-ring';
import { RenderPacer } from './sim/render-pacer';
import { monsterRevealed } from './render/monster-reveal';
import { TutorialRunner } from './tutorial/tutorial-runner';
import {
  getTutorialState, markTutorialStepSeen, skipTutorial, completeTutorial,
} from './net/tutorial-api';
import { t, N_ } from './i18n/i18n';

/** What `ability.used` carries — mirror of `sim/ability-broadcast.ts`'s payload. */
interface AbilityUsedMessage {
  playerId: string;
  abilityId: string;
  targetId?: string;
  serverTs: number;
  restoredHp?: number;
  /** The radius the effect actually swept, after talent potency. */
  radiusM?: number;
  /** How long the state it applied lasts, after talent potency. */
  durationMs?: number;
  hits?: { id: string; damage?: number; died?: boolean }[];
}

/**
 * Which archetypes draw something on the caster, and which draw something on
 * each body they landed on. Most draw one or the other, not both: a Mend's
 * motes belong on the squadmate who was healed, not on the druid, and an
 * Eviscerate's burst belongs on the skeleton.
 */
const CASTER_FX: ReadonlySet<AbilityFxKind> = new Set<AbilityFxKind>([
  'WARD', 'SHROUD', 'SHOUT', 'SWEEP', 'BOLT', 'LINK',
]);
const HIT_FX: Partial<Record<AbilityFxKind, AbilityFxKind>> = {
  STRIKE: 'STRIKE',
  // A pulse and a sweep are area effects: the ring shows the reach, a flash
  // on each body shows the catch.
  SHOUT: 'STRIKE',
  SWEEP: 'STRIKE',
  // The taunt's beam lands on something; punctuating it says which.
  LINK: 'STRIKE',
  BLESSING: 'BLESSING',
  SNARE: 'SNARE',
  // BOLT spawns its own impact when the mote arrives, which is later than
  // this message — a flash here would fire before the spell got there.
};
/** The two archetypes that visualise a state still in force, and so must last exactly as long as it does. */
const HELD_FX: ReadonlySet<AbilityFxKind> = new Set<AbilityFxKind>(['WARD', 'SNARE']);

/**
 * A held effect runs for the state's real duration; everything else runs for
 * however long reads well. A ward that faded at 900ms while the knight is
 * stagger-immune for 3s would be telling the player the wrong thing.
 */
function fxDurationFor(kind: AbilityFxKind, reportedDurationMs: number | undefined): number {
  return HELD_FX.has(kind) && reportedDurationMs ? reportedDurationMs : ABILITY_FX_DURATION_MS[kind];
}

/**
 * How long an ability's motion plays, in seconds — one number for all of
 * them, matching the 1.25s global cooldown so a clip is always finished
 * before the next press in a rotation can start another.
 */
const ABILITY_ANIMATION_SECONDS = 1.25;

/** Which animation an archetype plays on the body that cast it. */
const ABILITY_FX_INTENT: Record<AbilityFxKind, AnimIntent> = {
  WARD: 'ABILITY_WARD',
  SHROUD: 'ABILITY_STEALTH',
  SHOUT: 'ABILITY_SHOUT',
  SWEEP: 'ABILITY_SWEEP',
  STRIKE: 'ABILITY_STRIKE',
  BOLT: 'ABILITY_CAST',
  BLESSING: 'ABILITY_CAST',
  SNARE: 'ABILITY_CAST',
  LINK: 'ABILITY_SHOUT',
};

/**
 * design/gdd/dungeon-instance.md — the live match, rendered. Reuses the MVP
 * prototype's movement/combat wire protocol and math as-is (same input.x
 * and combat.result messages, same clock-sync handshake); the new parts
 * are real generated geometry (in place of bootstrap.ts's hardcoded
 * corridor) and the four world-state object types.
 *
 * Local-player rendering deliberately reuses RemoteInterpolator instead of
 * LocalPredictor/reconciliation: client-side prediction would need to
 * replicate the server's real-geometry collision (sim/spatial.ts) exactly,
 * which is real, separate work. Every player — local included — is rendered
 * ~1 broadcast tick + jitter buffer behind the server, same as a remote
 * player in bootstrap.ts. Input is still sent immediately, so the server
 * reacts without that delay; only the local render lags slightly.
 */

/**
 * design/gdd/waves.md — how close to the stair counts as standing on it.
 *
 * Mirrored from `waves-map.ts` rather than imported: the client does not share
 * a module graph with the server's game config. The server is the authority —
 * it refuses `waves.leave` with `not_on_the_stair` — so a drift here can only
 * ever make the prompt appear a little early or late, never let a run be
 * banked from somewhere it should not be.
 */
const WAVES_EXIT_RADIUS_M = 3;

const LEGEND = N_('WASD move · Shift sprint · Ctrl crouch · Space jump · LMB attack (J/K/L for L/OH/R) · F parry · R feint · RMB block (hold) · 1-9 abilities · Tab target · Q ability · E interact (hold to crank / climb out / revive) · G descend at an open gate · I bag · T lantern · Mouse look (click to capture, Esc to release) · MMB shoulder swap');
/** User-requested HP bars — floating just above a monster's head height. */
/**
 * The states a player may turn in — mirrors the server's own `canMove`
 * (backend movement-sim.ts), which is what actually gates `input.aim`. Kept as
 * a local mirror rather than imported: backend/ and frontend/ share no package,
 * the same hand-kept duplication `sim/predictor.ts` already documents.
 */
const TURNABLE_STATES = new Set<EntityStateName>(['IDLE', 'MOVING', 'SPRINTING', 'CROUCHING', 'BLOCKING']);

const MONSTER_HEALTH_BAR_HEIGHT_M = 2.3;
/**
 * Combat-feel amendment (2026-08-21) — user report: "combat still feels
 * stiff and wooden." Only the local player's own hits taken/landed trigger
 * these (a bystander's fight elsewhere shouldn't shake YOUR camera or
 * freeze YOUR screen) — `playImpactVfx()` on the defender's model fires
 * for every hit anyone can see, landed or not by the local player.
 */
const HIT_STOP_MS = 70;
const HIT_STOP_TIME_SCALE = 0.05;
const SHAKE_HIT_TAKEN_M = 0.12;
const SHAKE_HIT_LANDED_M = 0.05;
const SHAKE_DURATION_MS = 180;
/**
 * Warden's Route amendment (2026-08-24) — the old text ("loot the dungeon,
 * then crank open the exit gate") described a dungeon where the exit was
 * simply somewhere out there. It's now one route through the Bone Warden's
 * vault, so the objective says so: what you're heading for, what's in the
 * way, and that the side rooms are optional.
 */
/**
 * Inside this radius of a portcullis the compass points at the gate itself
 * rather than at the next route waypoint — close enough that a straight
 * line is a real instruction ("it's right there, hold E").
 *
 * Twice the interact distance, deliberately tight. This started at 20m and
 * was wrong: `resolveCompassTarget()` documents the measurement, but the
 * short version is that at 20m the arrow pointed through walls at an
 * unreachable gate in 4% of layouts. Anchoring it to
 * `MAX_INTERACT_DISTANCE_M` keeps it tied to the range at which the player
 * can actually *use* the thing being pointed at, rather than being a
 * free-floating number that can drift back up.
 */
const EXIT_DIRECT_BEARING_M = WORLD_STATE.MAX_INTERACT_DISTANCE_M * 2;

/**
 * design/gdd/run-start-flythrough.md — plays every run (layouts regenerate
 * per run, so there's no "already memorized it" case), skippable by any
 * movement key, a click/tap, or the overlay's own Skip button. Duration
 * comes from the server (`flythrough.durationMs`, Formula 2's fixed-target
 * playback speed) — these are only the camera's fixed viewing angles.
 */
const FLY_THROUGH_EYE_HEIGHT_M = 5;
const FLY_THROUGH_LOOKAT_HEIGHT_M = 1.4;

/** Mirrors the server's `PartyMemberPayload` — see `sendPartyStates` in dungeon-instance.room.ts. */
interface PartyMemberMessage {
  sessionId: string;
  nickname: string;
  characterClass: string;
  hp: number;
  hpMax: number;
  dead: boolean;
  /** Whether the body can still be picked up — see the server's own comment. */
  revivable: boolean;
  reviveEndsAt: number;
  x: number;
  z: number;
}

interface Interactable {
  id: string;
  x: number;
  z: number;
  /**
   * `downed` is a squadmate's body (2026-08-25) — the same E key, because
   * picking a teammate up is an interaction with the world like any other, and
   * a separate key for it would be a separate thing to learn.
   */
  kind: 'door' | 'portcullis' | 'chest' | 'brazier' | 'downed' | 'corpse';
  state: string;
  /** Only set for `downed` — whose body it is, for the prompt. */
  label?: string;
}

let currentTeardown: (() => void) | null = null;

/**
 * `reservation` (sconce-hub.md Core Rule #5): when provided, joins via
 * Matchmaking's `matchmaking.found` seat reservation instead of the direct
 * `joinOrCreate` dev bypass. `onMatchEnd` (sconce-hub.md Core Rule #8): fired
 * when the match room disconnects, so the hub screen can reconnect the
 * Socket.io gateway and remount itself — this scene has no "return to hub"
 * button of its own, the transition is driven entirely by the room lifecycle.
 */
export function mountDungeonInstance(
  root: HTMLElement,
  _user: AuthUser,
  _onLogout: () => void,
  reservation?: SeatReservation,
  /** `requeue` asks the hub to start searching immediately — the run-end screen's "Queue Again". */
  onMatchEnd?: (requeue?: boolean) => void,
): () => void {
  root.innerHTML = '';

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;display:block;';
  root.appendChild(canvas);

  /**
   * Same gap, same cover as the arena's — see ui/entry-veil.ts. Worse here, if
   * anything: a floor is a great deal more geometry than one room, and the
   * fly-through that opens a run is a *camera move*, so a player who arrives
   * mid-preview has already lost part of the one thing that shows them the way
   * out.
   */
  const entryVeil = new EntryVeil(root, t('Descending'), t('Finding the stair down\u2026'));

  const dScene = new DungeonInstanceScene(canvas);
  // Dev-only handle for headless perf runs (renderer.info draw calls, light
  // count) — the 2026-10-15 GPU pass was measured through it. Stripped from
  // production builds with the rest of `import.meta.env.DEV`.
  if (import.meta.env.DEV) (window as unknown as { __tallowScene?: DungeonInstanceScene }).__tallowScene = dScene;
  const input = new InputController();
  /**
   * Thumbs (2026-09-05). Null on anything with a mouse — see `isTouchDevice`.
   * It feeds `input.touchDirection` in the frame loop rather than owning a
   * second movement path.
   */
  const touch = isTouchDevice() ? new TouchControls(root) : null;
  const camera = new OverShoulderCameraController(canvas);
  const hud = new DebugHud(root, t(LEGEND), objectiveFor(1));
  const interactPrompt = new InteractPrompt(root);
  const lootPanel = new LootPanel(root);
  const lootToasts = new LootToasts(root);
  const damageFlash = new DamageFlash(root);
  const runEndOverlay = new RunEndOverlay(root);
  // Wired here rather than left to the default: on the real `/play` path this
  // hands control back to `session-bootstrap`'s hub<->match state machine. The
  // overlay's own fallback (navigate to /play) covers the direct-join dev path,
  // where `onMatchEnd` is undefined and the screen used to be a dead end.
  if (onMatchEnd) {
    runEndOverlay.onReturnToHub = () => { currentTeardown?.(); onMatchEnd(); };
    runEndOverlay.onQueueAgain = () => { currentTeardown?.(); onMatchEnd(true); };
  }
  const goalCompass = new GoalCompass(root);
  const actionBar = new ActionBar(root);
  const combatHud = new CombatHud(root);
  const damageNumbers = new DamageNumbers(root);
  const flyThroughOverlay = new FlyThroughOverlay(root);

  /*
   * Player preferences, bound for the life of the run.
   *
   * Bound rather than read once: the settings screen is reachable from the run
   * menu, so a sensitivity changed mid-run has to take effect on the next mouse
   * movement rather than on the next match.
   */
  const unbindSettings: Array<() => void> = [
    bindCameraToSettings(camera),
    bindGameplayToSettings((g) => {
      damageNumbers.setEnabled(g.damageNumbers);
      damageFlash.setEnabled(g.hitFlash);
      hud.setDetailVisible(g.showPerformance);
    }),
  ];
  // camera-and-controls.md amendment (2026-08-25, fifth) — mouse-look needs the
  // pointer captured, and a player whose mouse does nothing needs to be told why.
  const levelUpBanner = new LevelUpBanner(root);
  const downedOverlay = new DownedOverlay(root);
  // User: "there is no menu and no way to manually quit the game."
  const runMenu = new RunMenu(root, {
    onAbandon: () => {
      room?.send('run.abandon');
      hud.logCombatEvent('LEAVING THE DUNGEON…');
    },
    // Movement keys are read from a held-key set; releasing it on open stops
    // a player walking into a wall while the menu is up.
    onVisibilityChange: (open) => { if (open) { input.releaseAll(); touch?.releaseAll(); } },
  }, t(LEGEND));
  const abilityNotice = new AbilityNotice(root);
  // A phone has no Tab: refusals name the Target plate instead (2026-10-16).
  abilityNotice.touch = touch !== null;
  const questBanner = new QuestCompleteBanner(root);
  const mouseLookPrompt = new MouseLookPrompt(root);
  /**
   * The delve half of the tutorial (design/gdd/tutorial.md).
   *
   * Contextual rather than scripted: there is no tutorial dungeon, and there
   * should not be one — this is an extraction game, and a run with nothing at
   * stake teaches the opposite of the lesson. Every step here hangs off a
   * signal a real run emits anyway, so the chapter plays inside whatever the
   * player actually queued into.
   *
   * `null` until the account's progress arrives, and for the whole run after
   * that if they have finished or skipped. Held in a box rather than a bare
   * `let` because it is assigned inside a promise callback, and TypeScript
   * keeps the `null` narrowing inside every closure created after such a
   * declaration — which here is most of the file.
   */
  const tutorial: { runner: TutorialRunner | null } = { runner: null };
  const signalTutorial = (name: Parameters<TutorialRunner['signal']>[0]): void => tutorial.runner?.signal(name);
  const pauseTutorial = (paused: boolean): void => tutorial.runner?.setPaused(paused);
  camera.onLockChange = (locked) => mouseLookPrompt.setLocked(locked);
  mouseLookPrompt.setLocked(false);
  // Painted effect sprites (2026-08-26). Loaded here and owned by the layer;
  // if any of them fail to arrive the layer falls back to its procedural
  // shapes, so an effect is never silently invisible.
  const abilityFx = new AbilityFxLayer(dScene.scene, loadAbilityFxSprites());
  /** The floor mark under the current target — see `render/target-ring.ts`. */
  const targetRing = new TargetRing(dScene.scene);
  const audioEngine = new AudioEngine();
  unbindSettings.push(bindAudioToSettings(audioEngine));
  const remoteFootsteps = new RemoteFootsteps();
  /** Jump (2026-10-16) — the local player's predicted takeoff; see `sim/jump.ts`. */
  const localJump = new LocalJump();
  /** Set on room join; the frame loop needs it to pick footstep materials. */
  let currentStratum = 1;
  audioEngine.ensureStarted(); // arms resume-on-gesture listeners immediately; actual playback stays silent until a real user gesture resumes the context
  input.start();
  camera.start();

  const visuals = new Map<string, CharacterVisual>();
  const remoteInterpolators = new Map<string, RemoteInterpolator>();
  // design/gdd/skeleton-ai.md's Bone Warden amendment (2026-08-20) — monsters
  // were never rendered before this; separate maps from players' own, keyed
  // by monster id (`monster_0`, ...) rather than a Colyseus sessionId.
  const monsterVisuals = new Map<string, CharacterVisual>();
  /** Monsters whose rim and HP bar are currently showing — the hysteresis memory for `monsterRevealed`. */
  const monsterRevealedIds = new Set<string>();
  const monsterInterpolators = new Map<string, RemoteInterpolator>();
  // HP bars: no shared max-HP-by-archetype constant exists client-side (and
  // stratum HP multipliers live server-side only) — a monster's first-seen
  // hp value at spawn IS its max, so this is simpler and inherently correct
  // even if server-side HP scaling changes later.
  /** Reused every frame by the damage-number projector — see dungeon-instance-scene.ts's no-per-frame-allocations note. */
  const projectVec = new THREE.Vector3();

  let disposed = false;
  /** Set once the floor's geometry is in the scene — half of what the entry veil waits on. */
  let layoutReady = false;
  /** See the arena's identical counter: two frames, so the veil never lifts on a body still at the origin. */
  let framesWithWorld = 0;
  /** The veil's own backstop; see the arena's note on why waiting forever is the worse failure. */
  const veilDeadlineMs = performance.now() + 15_000;
  let room: Room | null = null;
  let clockSync: ClientClockSync | null = null;

  /**
   * Where an entity is *right now*, for an effect to follow.
   *
   * A getter rather than a position, because everything an effect is drawn on
   * keeps moving: a rooted skeleton is still being shoved by knockback, a
   * healed squadmate keeps running, and a 3-second ward has to stay on the
   * knight for all three seconds. It also reports the entity going away —
   * interest management culls bodies mid-effect, and `ability-fx.ts` ends an
   * effect whose anchor returns null rather than leaving it in mid-air.
   */
  const anchorFor = (id: string) => () => {
    const entity = room?.state.players.get(id) ?? room?.state.monsters.get(id);
    return entity ? { x: entity.x, z: entity.z } : null;
  };
  let localSessionId: string | null = null;
  /**
   * The shutter the whole world is photographed through — every body, and the
   * camera that follows one of them, is drawn from a sample taken at this
   * time. See sim/render-clock.ts for the jitter that came of resetting it
   * outright on every patch.
   */
  const renderClock = new ServerRenderClock(performance.now());
  let lastSentDirection = { x: 0, z: 0 };
  let lastMoveSentAt = 0;
  // camera-and-controls.md amendment (2026-08-25, fifth) — mouse-look aim.
  // Sent on its own cadence: the mouse moves continuously, and the server only
  // needs it often enough that a swing lands where the player was looking.
  let lastSentAimYaw: number | null = null;
  let lastAimSentAt = 0;
  // The camera is pointed at the spawn facing exactly once, the first time the
  // local player's own state arrives — see OverShoulderCameraController.alignTo.
  let cameraAligned = false;
  let activeCrankTargetId: string | null = null;
  /** The body currently being revived, and when the channel completes — see `party.revive_channel`. */
  let activeReviveTargetId: string | null = null;
  let reviveEndsAt = 0;
  let reviveStartedAt = 0;
  // Multi-stratum amendment — set by 'stratum.descend' just before the server
  // calls client.leave() on its end; onLeave below checks it to remount into
  // the next floor instead of firing onMatchEnd (which would bounce to the hub).
  let descendReservation: SeatReservation | null = null;
  // "no clear goals" / damage-feedback fixes below need the local player's
  // previous hp to detect a hit, and it must survive across re-renders.
  let lastLocalHp: number | null = null;
  // Game Feel Polish Phase 3 — loot.inventory sends the FULL current
  // inventory on every change (pickup, drop, insure/uninsure), not a delta;
  // only an actual increase should play the "got item" chime.
  let lastLootItemCount = -1;
  /**
   * Which item ids were in the bag on the previous snapshot (2026-09-07).
   *
   * `loot.inventory` is a full snapshot, not a delta, so "what just arrived"
   * has to be diffed here rather than read off the message. Diffing by
   * `itemId` rather than by count also gets multi-item changes right — a
   * chest that yields while a drop is in flight, for instance.
   *
   * `null` until the first snapshot lands, and deliberately so: the first one
   * is a *baseline*, not a set of pickups. Toasting it would announce the
   * whole bag at the start of every descent, since a descending player
   * arrives in the next floor's room already carrying everything.
   */
  let lastLootItemIds: Set<string> | null = null;
  // MMO combat integration (2026-08-24) — the server's per-player ability
  // state. (Tab's running index is gone — see `cycleTarget`.)
  let abilityState: AbilityStateView | null = null;
  /**
   * Combat-legibility slice (2026-08-24) — the client never tracked its own
   * target. `cycleTarget` sent `combat.target` and forgot it, so there was no
   * way to draw a target frame for the thing you had selected.
   *
   * Set by Tab, and also implicitly by fighting: the last monster you traded
   * blows with becomes the frame's subject if you never pressed Tab. Most
   * players will not press Tab, and "what am I hitting" has to be answered
   * for them too.
   */
  let localTargetId = '';
  let localTargetIsExplicit = false;

  /**
   * Last `party.state` payload. Empty until the first one arrives (up to
   * PARTY_STATE_INTERVAL_MS after joining), which just means the party panel
   * appears a beat late rather than showing a wrong roster.
   */
  let partyMembers: PartyMemberMessage[] = [];
  /** Session ids in the order the party frames draw them (role-sorted) — what F2–F4 index into. */
  let partyFrameOrder: string[] = [];
  /** Target refusals this run, for the one-shot hint in the `input.rejected` handler. */
  let targetRefusals = 0;
  const TARGET_REFUSALS = new Set(['needs_target', 'out_of_range', 'not_visible', 'no_line_of_sight', 'invalid_target']);

  /**
   * Whether a session id belongs to the local player's squad.
   *
   * `squadId` has been on `PlayerState` since squads landed and the client had
   * never read it: friendly fire and squad-only healing were both enforced
   * server-side and completely invisible here. It drives the party list and
   * decides whose damage is worth floating a number for.
   */
  function isSquadmate(sessionId: string): boolean {
    if (sessionId === localSessionId) return false;
    // Answered from the `party.state` roster, not from `room.state.players`.
    // The schema is interest-managed, so asking it means "is this a squadmate I
    // can currently see" — which silently answers no for the exact squadmate
    // whose hit you most want labelled correctly.
    return partyMembers.some((member) => member.sessionId === sessionId);
  }

  /**
   * Tab-cycles the soft target over hostile monsters in view, nearest first.
   * Only monsters: the spec's targeting exists to give abilities a subject,
   * and a Tab that could silently land on a squadmate would make a damage
   * ability refuse for reasons the player cannot see.
   */
  function cycleTarget(): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me) return;
    // 2026-10-16 — `pickCycleTarget` (sim/targeting.ts): nearest first, then
    // past the current one, and only what the player can actually see. The
    // old running index skipped the nearest and drifted as the list re-sorted.
    // An implicit target (a swing, an ability's auto-pick) is not a place in
    // the cycle: the first Tab starts from the nearest (QA defect C).
    const next = pickCycleTarget(enemyCandidates(), me, localTargetIsExplicit ? localTargetId : '');
    if (!next) {
      setLocalTarget('', false);
      abilityNotice.showText(t('Nothing you can see to target'));
      return;
    }
    setLocalTarget(next, true);
    signalTutorial('run:targeted');
  }

  /**
   * The one place the soft target changes (2026-10-16). Every path used to
   * set `localTargetId` itself and only some of them told the server — the
   * implicit target from a landed swing never did, so the frame showed one
   * monster while abilities went to another, or were refused with "No target".
   */
  function setLocalTarget(id: string, explicit: boolean): void {
    localTargetIsExplicit = explicit && id !== '';
    if (id === localTargetId) return;
    localTargetId = id;
    if (room) sendTarget(room, id);
  }

  /** Live monsters as targeting candidates, `revealed` from the monster-reveal set. */
  function enemyCandidates(): TargetCandidate[] {
    if (!room) return [];
    const out: TargetCandidate[] = [];
    for (const [id, m] of room.state.monsters.entries()) {
      out.push({ id, x: m.x, z: m.z, alive: m.state !== 'DEAD', revealed: monsterRevealedIds.has(id) });
    }
    return out;
  }

  /** The squad in party-frame order, for F2–F4. Mirrors the order `setParty` is fed. */
  function selectPartyMember(index: number): void {
    if (!room || !localSessionId) return;
    const id = partyKeyTarget(index, localSessionId, partyFrameOrder);
    if (!id) return;
    setLocalTarget(id, true);
    signalTutorial('run:targeted');
  }

  /**
   * Give an ability a subject, so pressing 2 or 3 does something.
   *
   * User report: "when I press skills 2 and 3, it displays 'No target', but I
   * don't understand how to select a target or cast these skills." Tab has
   * always cycled targets, and nothing ever said so — the refusal named the
   * problem and not the remedy, on a key the player had no reason to know
   * existed.
   *
   * So an enemy-targeted ability now picks the nearest live monster itself if
   * the player has not chosen one. No distance bound is needed: interest
   * management only puts monsters in `room.state` within the 10m lit / 4m dark
   * sight radius, so anything visible here is already in the fight.
   *
   * Marked NON-explicit, so this stays a convenience rather than a lock —
   * implicit targeting can still move it to whatever you actually trade blows
   * with, and Tab still overrides. Self- and ally-targeted abilities are
   * unaffected: the server ignores the target field for those.
   */
  function acquireTargetForAbility(slot: number): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me) return;
    // 2026-10-16 — only for ENEMY abilities, and only what the player can see,
    // reach and is facing (`pickAbilityTarget`). It used to grab the nearest
    // monster for every press — heals included — even one still hidden in the
    // dark, and replace a squadmate the player had selected for a heal.
    const ability = abilityState?.abilities.find((a) => a.slot === slot);
    if (!ability) return;
    const next = pickAbilityTarget({
      targeting: ability.targeting ?? 'ENEMY',
      rangeM: ability.rangeM,
      candidates: enemyCandidates(),
      me,
      currentId: localTargetId,
      currentIsExplicit: localTargetIsExplicit,
    });
    if (next) setLocalTarget(next, false);
  }

  /**
   * The nearest enemy an ENEMY ability would act on right now — the current
   * one if it is alive and seen, else the best visible pick — for the action
   * bar's range tint. Null when there is nothing.
   */
  function enemyDistanceForBar(me: { x: number; z: number; facing?: number }): number | null {
    const candidates = enemyCandidates();
    const current = candidates.find((c) => c.id === localTargetId && c.alive && c.revealed);
    if (current) return Math.hypot(current.x - me.x, current.z - me.z);
    const pick = pickCycleTarget(candidates, me, '');
    const best = pick ? candidates.find((c) => c.id === pick) : undefined;
    return best ? Math.hypot(best.x - me.x, best.z - me.z) : null;
  }

  // design/gdd/run-start-flythrough.md — fly-through state. Movement input,
  // camera follow, and world-action input are all gated on flyThroughActive.
  let flyThroughActive = false;
  let flyThroughElapsedMs = 0;
  let flyThroughDurationMs = 0;
  /** design/gdd/waves.md — the exit pad's world position, or null in a delve. */
  let wavesPad: { x: number; z: number } | null = null;
  /** Throttle for the wave line — see where it is written. */
  let lastWavesLineAt = 0;
  let flyThroughWaypoints: { x: number; z: number }[] = [];

  function endFlyThrough(): void {
    if (!flyThroughActive) return;
    flyThroughActive = false;
    flyThroughOverlay.hide();
    pauseTutorial(false);
    mouseLookPrompt.setSuppressed(false);
  }
  flyThroughOverlay.onSkip = () => endFlyThrough();

  /**
   * Game-feel polish plan Phase 1 — the local player's own action animations
   * (attack/dodge/parry/feint/block) used to only start once the
   * server's state change round-tripped back over the network: `visuals.get()
   * .setState()` was called exclusively from `$(player).onChange()`, with no
   * exception for "this is me." At this stack's tuning (BROADCAST_HZ=20,
   * JITTER_BUFFER_MS=30) that's a flat ~130ms of input-to-animation latency
   * before any real network RTT on top — likely the actual root cause behind
   * "combat still feels stiff and wooden" (see character-visuals-tuning.ts's
   * 2026-08-21 amendment note), not something hit-stop/shake/flash alone can
   * fix (those all fire on the *result*, not the swing itself).
   *
   * This predicts the STATE MACHINE only, not position — deliberately not
   * the harder, separately-flagged movement-position prediction problem
   * (see this file's own top-of-function comment on why that's deferred).
   * Every predicted state maps to the same animGroup the server's own later
   * echo will resolve to (character-visual.ts's `setState()` already no-ops
   * on an unchanged group), so the predicted animation is never restarted or
   * fought by the real update — it just already started up to a full
   * broadcast tick + jitter buffer earlier. On the rare disagreement (a
   * rejected/insufficient-stamina action, or an interrupt that raced in), the
   * server's own next echo carries a genuinely different state and corrects
   * the visual via the existing cross-fade, exactly the "reconcile the
   * result, never fight the animation" rule this whole system already
   * follows for remote players.
   */
  /**
   * Space / the touch Jump plate (2026-10-16). Predicted like every other
   * local action — the hop starts this frame, and the server's echo only
   * prices it. A refusal (`input.rejected` with `action: 'jump'`) lands the
   * body early via `localJump.cancel()`.
   */
  function jumpLocal(): void {
    if (!room || !localSessionId) return;
    const me = room.state.players.get(localSessionId);
    if (!me || !localJump.tryStart(estimatedServerTimeNow(), me.state, me.stamina)) return;
    visuals.get(localSessionId)?.playJump(JUMP_DURATION_MS);
    sendJump(room);
  }

  function predictLocalState(nextState: EntityStateName, attackDirection?: string): void {
    if (!room || !localSessionId) return;
    const localPlayer = room.state.players.get(localSessionId);
    if (!localPlayer || localPlayer.state === 'DEAD' || localPlayer.state === 'DISCONNECTED') return;
    visuals.get(localSessionId)?.setState(nextState, localPlayer.weaponClass as WeaponClass, attackDirection);
  }

  /**
   * Sends the current aim right now, ignoring the 20Hz cadence below.
   *
   * Called immediately before an attack, parry or block. The server stops
   * accepting aim for the duration of a committed action (`handleAim`'s
   * `canMove` gate), so whatever aim it holds at the instant the action arrives
   * is the facing the whole swing resolves against — up to 50ms stale on the
   * normal cadence, which at a walking pace is a couple of degrees and at the
   * end of a fast mouse flick is a lot more than that.
   */
  function flushAim(): void {
    if (!room) return;
    lastSentAimYaw = camera.viewYaw;
    lastAimSentAt = performance.now();
    sendAim(room, camera.viewYaw);
  }

  /**
   * Which way to *draw* the local player's own body this frame.
   *
   * The mouse turns the camera on the frame it moves; the server hears about it
   * a round trip later. Drawing the local body from the interpolated schema
   * alone would leave it visibly trailing the view — and this is the one entity
   * on screen where that is unmissable, because the player is holding the input
   * that caused it. So it is predicted straight from the camera, but only while
   * the player is in a state that may still turn. During a committed action
   * (a swing, a parry, a dodge) the server's own facing wins: that is precisely
   * when the server has stopped accepting aim, and a body that kept turning
   * would be lying about where its hit arc points.
   */
  function localRenderFacing(serverFacing: number): number {
    const state = room && localSessionId ? room.state.players.get(localSessionId)?.state : undefined;
    return state && TURNABLE_STATES.has(state as EntityStateName) ? camera.viewYaw : serverFacing;
  }

  // "It would probably be more convenient to use the left mouse button for
  // melee attacks" — LMB now always swings OVERHEAD (melee-combat.md Core
  // Rule #2: direction orients the hit-arc but never gates parry success, so
  // collapsing to one default direction loses no defensive depth); J/K/L
  // remain for players who want to pick a specific swing direction.
  const onCanvasMouseDown = (e: MouseEvent): void => {
    if (flyThroughActive) { endFlyThrough(); return; }
    // camera-and-controls.md amendment (2026-08-25, fifth) — the click that
    // captures the pointer must not also be a swing. Every run starts, and
    // every Escape ends, with the pointer free; a first click that both grabbed
    // the mouse and committed a 420ms attack would be a free hit handed to
    // whatever is standing in front of you. `requestPointerLock` resolves in a
    // later task than this handler runs in, so on the capturing click this flag
    // is reliably still false.
    //
    // **Amended 2026-09-05.** `lookUnavailable` is the escape hatch, and it is
    // the difference between deferring one click and disarming the mouse. When
    // the browser refuses Pointer Lock — Chrome's post-Escape cooldown, or an
    // embed that does not implement it — `pointerLocked` never becomes true,
    // so *every* click was swallowed and the only way left to attack was
    // J/K/L. The player reported it as "make LMB attack", which it already
    // did, right up until the moment it quietly stopped.
    if (!camera.pointerLocked && !camera.lookUnavailable) return;
    if (e.button === 0) {
      if (room) { flushAim(); predictLocalState('ATTACK_WINDUP', 'OVERHEAD'); sendAttack(room, 'OVERHEAD'); }
    } else if (e.button === 2) {
      // combat-depth.md (2026-08-21) — hold-to-block on RMB; the canvas's
      // own contextmenu prevention (wired by OverShoulderCameraController)
      // already suppresses the right-click menu, so this is a clean hold input
      // like Shift/Ctrl.
      if (room) { flushAim(); predictLocalState('BLOCKING'); sendBlock(room, true); }
    }
  };
  const onCanvasMouseUp = (e: MouseEvent): void => {
    if (flyThroughActive) return;
    if (e.button === 2 && room) sendBlock(room, false);
  };
  canvas.addEventListener('mousedown', onCanvasMouseDown);
  canvas.addEventListener('mouseup', onCanvasMouseUp);

  // insurance.md — capacity is account-level, fetched once; doesn't change mid-run.
  const insuranceStatus = getInsuranceStatus();
  insuranceStatus.then((status) => lootPanel.setInsuranceSlots(status.insuranceSlots)).catch(() => {});

  /**
   * Start the delve chapter, if this account still has one to run.
   *
   * Both requests are already in flight for other reasons, so this costs one
   * extra round trip, not three. Cover is part of the context because the
   * "mark what you cannot lose" step is a lie to a player with no charges
   * left — there is nothing they can mark.
   */
  void Promise.all([getTutorialState(), insuranceStatus.catch(() => null)])
    .then(([state, insurance]) => {
      if (disposed || state.skipped || state.completedAt) return;
      tutorial.runner = new TutorialRunner({
        chapter: 'delve',
        container: root,
        seen: state.seen,
        skipped: false,
        context: {
          characterCount: 1,
          insuranceCharges: insurance?.charges ?? 0,
          hasKeyboard: !!window.matchMedia?.('(pointer: fine)').matches,
          // A getter: the class is only known once the room has synced, and
          // the runner re-reads the context before every step.
          get isHealer(): boolean {
            const me = localSessionId ? room?.state.players.get(localSessionId) : undefined;
            return !!me && rosterEntry(me.characterClass).role === 'HEALER';
          },
        },
        persistence: { markSeen: markTutorialStepSeen, skip: skipTutorial, complete: completeTutorial },
        // Two steps point at the bag, and one of them ("Mark what you cannot
        // lose") asks the player to press a button inside it. Since the bag
        // collapsed to a summary bar (2026-09-07) that button is not on
        // screen, so the step that needs it opens it.
        onStepShown: (stepId) => {
          if (stepId === 'loot' || stepId === 'insure-item') lootPanel.setExpanded(true);
        },
      });
      // The opening fly-through drives the camera itself and already carries a
      // Skip button; a second overlay competing with it would be two things
      // asking for the same press.
      tutorial.runner.setPaused(flyThroughActive);
      tutorial.runner.start();
    })
    .catch(() => {});

  /**
   * Which emotes this player owns, and what to call them.
   *
   * Fetched over REST rather than pushed by the room: it is account state that
   * cannot change mid-run, exactly like `insuranceSlots` above. The server
   * re-checks ownership on every `input.emote` regardless, so this list only
   * decides what the menu offers, never what is allowed.
   *
   * Labels are derived from the catalog's own clip name (`Push_Ups` ->
   * "Push Ups") rather than kept in a table here — the same "numbers and names
   * come from the server" split the talent tree and the satchel both use.
   */
  const emoteMenu = new EmoteMenu(root);
  Promise.all([getCosmeticCatalog(), getMyCosmetics()])
    .then(([catalog, mine]) => {
      const owned = new Set(mine.map((item) => item.cosmeticDefId));
      emoteMenu.setOptions(
        catalog
          .filter((def) => def.kind === 'EMOTE' && owned.has(def.id))
          .map((def) => ({ id: def.id, label: def.name ? t(def.name) : (def.clipName ?? def.id).replace(/_/g, ' ') })),
      );
    })
    .catch(() => {});
  lootPanel.onDrop = (itemId) => room && sendLootDrop(room, itemId);
  lootPanel.onInsure = (itemId) => room && sendLootInsure(room, itemId);
  lootPanel.onUninsure = (itemId) => room && sendLootUninsure(room, itemId);

  function estimatedServerTimeNow(): number {
    return renderClock.now(performance.now());
  }

  /**
   * Whether the server would accept a revive on this body right now.
   *
   * Read from the `party.state` roster rather than the schema, deliberately:
   * the roster is NOT interest-gated (see `sendPartyStates`), so it still
   * answers for a squadmate who has gone dark — which is exactly when a player
   * needs to know whether running back is worth it. `reviveEndsAt` is checked
   * too, because the roster arrives at 4Hz and the flag can be a quarter of a
   * second stale at the very end of the window.
   */
  function isRevivableNow(sessionId: string): boolean {
    const member = partyMembers.find((m) => m.sessionId === sessionId);
    if (!member || !member.revivable) return false;
    return member.reviveEndsAt - estimatedServerTimeNow() > 0;
  }

  function findNearestInteractable(): Interactable | null {
    if (!room || !localSessionId) return null;
    const me = room.state.players.get(localSessionId);
    if (!me) return null;

    let nearest: (Interactable & { dist: number }) | null = null;
    const consider = (id: string, x: number, z: number, kind: Interactable['kind'], state: string) => {
      const dist = Math.hypot(me.x - x, me.z - z);
      if (dist > WORLD_STATE.MAX_INTERACT_DISTANCE_M) return;
      if (!nearest || dist < nearest.dist) nearest = { id, x, z, kind, state, dist };
    };
    for (const [id, d] of room.state.doors.entries()) consider(id, d.x, d.z, 'door', d.state);
    for (const [id, p] of room.state.portcullises.entries()) consider(id, p.x, p.z, 'portcullis', p.state);
    for (const [id, c] of room.state.chests.entries()) consider(id, c.x, c.z, 'chest', c.state);
    for (const [id, b] of room.state.braziers.entries()) consider(id, b.x, b.z, 'brazier', b.state);
    // Downed squadmates. Only your own squad, and only bodies the server says
    // are still savable — a rival delver's is not yours to pick up, and a
    // squadmate whose window has closed is a body that will never get up.
    //
    // That last clause is the fix for "why can't I revive the bots?" (see the
    // server's `PartyMemberPayload.revivable`). A finalised body stays in the
    // world by design, so `state === 'DEAD'` alone kept offering "hold E to
    // revive" long after the server would only ever answer `not_downed`. A
    // prompt that promises what the keypress cannot deliver is worse than no
    // prompt: it reads as the revive being broken.
    // Bodies you can still search (2026-09-07). Gated on the server's own
    // `lootState` rather than on `state === 'DEAD'`, for the same reason the
    // revive prompt is gated on `revivable`: a prompt that promises what the
    // keypress cannot deliver reads as the mechanic being broken. A body that
    // was carrying nothing, and one somebody has already emptied, both say
    // nothing at all.
    for (const [id, m] of room.state.monsters.entries()) {
      if (m.lootState !== 'LOOTABLE') continue;
      consider(id, m.x, m.z, 'corpse', m.lootState);
    }
    for (const [id, p] of room.state.players.entries()) {
      if (id === localSessionId || p.state !== 'DEAD') continue;
      if (!p.squadId || p.squadId !== me.squadId) continue;
      if (!isRevivableNow(id)) continue;
      const dist = Math.hypot(me.x - p.x, me.z - p.z);
      if (dist > WORLD_STATE.MAX_INTERACT_DISTANCE_M) continue;
      if (!nearest || dist < nearest.dist) {
        nearest = { id, x: p.x, z: p.z, kind: 'downed', state: p.state, dist, label: p.nickname };
      }
    }
    return nearest;
  }

  /** design/gdd/waves.md Core Rule #4 — in a lull, standing on the stair. */
  function canLeaveWaves(): boolean {
    if (!room || !wavesPad || !localSessionId) return false;
    if (room.state.wavesPhase !== 'LULL') return false;
    const me = room.state.players.get(localSessionId);
    if (!me || me.state === 'DEAD') return false;
    return Math.hypot(me.x - wavesPad.x, me.z - wavesPad.z) <= WAVES_EXIT_RADIUS_M;
  }

  function handleInteractStart(): void {
    if (!room) return;
    // Checked before anything else in reach: the stair sits in an open room,
    // and a corpse that rolled onto it must not be able to eat the one press
    // that banks the run.
    if (canLeaveWaves()) { sendWavesLeave(room); return; }
    const target = findNearestInteractable();
    if (!target) {
      hud.logCombatEvent('nothing in range');
      // Said to the player too (2026-10-16): this used to reach only the
      // developer log, so an E pressed a step too far did nothing, silently.
      abilityNotice.showText(t('Nothing to use within reach'));
      return;
    }
    switch (target.kind) {
      case 'door':
        if (target.state === 'BARRED') sendDoorUnbar(room, target.id); else sendDoorBar(room, target.id);
        break;
      case 'portcullis':
        // run-lifecycle.md Core Rule #3 — an OPEN portcullis has nothing left
        // to crank (Dungeon Instance's Edge Case #5 would silently ignore a
        // crank_start here anyway); interacting with it is how you extract.
        if (target.state === 'OPEN') {
          sendExtract(room, target.id);
        } else {
          activeCrankTargetId = target.id;
          sendCrankStart(room, target.id);
        }
        break;
      case 'chest':
        sendChestOpen(room, target.id);
        break;
      case 'corpse':
        sendCorpseLoot(room, target.id);
        break;
      case 'brazier':
        if (target.state === 'LIT') sendBrazierDouse(room, target.id); else sendBrazierLight(room, target.id);
        break;
      case 'downed':
        // Optimistic only in the sense that the HUD line appears immediately;
        // `party.revive_channel` is what supplies the real timing, and a
        // rejection clears it.
        activeReviveTargetId = target.id;
        reviveStartedAt = performance.now();
        reviveEndsAt = 0;
        sendReviveStart(room, target.id);
        break;
    }
  }

  /**
   * The gate you are cranking has just opened, and you are still holding E.
   *
   * User: *"I completed the entire dungeon, reached the exit, held E, and the
   * red exit bar simply disappeared, but nothing happened."*
   *
   * Exactly what the code did. `handleInteractStart` fires on key*down* only,
   * and branches on the portcullis state at that instant: SEALED means crank,
   * OPEN means leave. Hold E through the crank and there is never a second
   * keydown — the bar fills, the gate opens, the branch that would have sent
   * `run.extract` is never reached again, and releasing E only sends a
   * `crank_stop` the server correctly no-ops. You had to release and press
   * again, and nothing said so; the objective banner even said "hold E next
   * to it to extract", which is precisely the input that could not work.
   *
   * The fix is *not* to fire the extract from here. That would honour the old
   * wording, and it would also drag a squad's designated cranker down a floor
   * alone the instant the bar filled — they are holding E to open the gate for
   * everyone else, not to leave through it. Leaving is a decision, and it
   * keeps its own keypress.
   *
   * What this does instead is end the *crank*, the moment there is nothing
   * left to crank. That releases the HUD line, so "cranking: 100%" gives way
   * to "press E to descend a floor" while the key is still held down — the
   * sentence that was missing, arriving at the exact moment it is needed.
   * `handleInteractEnd`'s `crank_stop` is not owed: the server already drops
   * every cranker on completion (`completeCrank`).
   */
  function pollCrankedGateOpened(): void {
    if (!room || !activeCrankTargetId) return;
    if (room.state.portcullises.get(activeCrankTargetId)?.state !== 'OPEN') return;
    activeCrankTargetId = null;
  }

  /**
   * G at an open gate: go deeper instead of banking the run.
   *
   * Its own key rather than a second meaning for E, because E is the reflex
   * press and the reflex press must be the one that keeps your loot. See
   * `ui/run-objective.ts` for why the floor stopped deciding this.
   *
   * Silent when there is no gate in reach, unlike E's "nothing in range" — G
   * is not a general interact key, so a stray press is a stray press rather
   * than a failed action worth reporting. It *does* answer on the Vaults,
   * where a player might reasonably expect to be able to go deeper and there
   * is nothing down there.
   */
  function handleDescendPress(): void {
    if (!room) return;
    const target = findNearestInteractable();
    if (!target || target.kind !== 'portcullis' || target.state !== 'OPEN') return;
    if (isFinalFloor(currentStratum)) {
      hud.logCombatEvent('nothing below the Vaults — press E to climb out');
      abilityNotice.showText(touch
        ? t('Nothing lies below the Vaults — tap Use to climb out')
        : t('Nothing lies below the Vaults — press E to climb out'), 3_000);
      return;
    }
    sendExtract(room, target.id, 'DOWN');
  }

  function handleInteractEnd(): void {
    if (!room) return;
    if (activeReviveTargetId) {
      sendReviveStop(room);
      activeReviveTargetId = null;
      reviveEndsAt = 0;
    }
    if (!activeCrankTargetId) return;
    sendCrankStop(room, activeCrankTargetId);
    activeCrankTargetId = null;
  }

  (async () => {
    const joined = reservation ? await joinDungeonInstanceViaReservation(reservation) : await joinDungeonInstance();
    if (disposed) {
      joined.room.leave();
      return;
    }
    room = joined.room;
    clockSync = joined.clockSync;
    localSessionId = room.sessionId;
    // Stable reference for closures below — avoids depending on the render
    // clock's own `serverTime` listener having already run in this same patch
    // (see the interpolator snapshot comment below).
    const roomState = room.state;

    hud.logCombatEvent(`stratum ${joined.layout.stratum} — seed=${joined.layout.seed} rooms=${joined.layout.roomCount}${joined.layout.isFallback ? ' (fallback)' : ''}`);
    currentStratum = joined.layout.stratum;
    hud.setObjective(objectiveFor(currentStratum));
    audioEngine.startAmbient(joined.layout.stratum);
    entryVeil.setStep(t('Laying the stone\u2026'));
    const group = await buildInstancedLayout(joined.layout.instances, paletteForStratum(joined.layout.stratum));
    if (disposed) return;
    dScene.setLayoutGroup(group, joined.layout.instances);
    // Not awaited: shaders compile in parallel behind the veil and the
    // fly-through, rather than on the frame the first skeleton walks in.
    void dScene.warmUp();
    layoutReady = true;
    entryVeil.setStep(t('Lighting the way\u2026'));

    // Edge Case #1 — a null/empty flythrough (defensively unreachable path)
    // skips straight to normal camera/input; flyThroughActive simply never flips.
    /**
     * design/gdd/waves.md Core Rule #4 — where the way out is.
     *
     * Read off the `stairs` instance in the layout rather than carried as its
     * own wire field: the pad IS that model, so taking its position from the
     * geometry means the prompt can never disagree with the thing the player
     * is looking at. Null in a delve, which has no such prop.
     */
    const stair = joined.layout.instances.find((i) => i.model === 'stairs');
    wavesPad = stair ? { x: stair.x, z: stair.z } : null;

    flyThroughWaypoints = joined.layout.flythrough?.waypoints ?? [];
    // A player who has asked to skip it is treated exactly as if they had hit a
    // key on the first frame — the preview is skippable by any input anyway, so
    // this saves the input, not a rule.
    if (flyThroughWaypoints.length >= 2 && !gameplayPreferences().autoSkipFlyThrough) {
      flyThroughActive = true;
      pauseTutorial(true);
      flyThroughElapsedMs = 0;
      flyThroughDurationMs = joined.layout.flythrough!.durationMs;
      flyThroughOverlay.show();
      // The preview drives the camera itself, and the click that skips it is
      // not the click that captures the pointer — so "click to look" would be
      // both wrong and competing with the Skip button.
      mouseLookPrompt.setSuppressed(true);
    }

    const $ = getStateCallbacks(room);

    $(room.state).listen('serverTime', (value: number) => {
      renderClock.onServerTime(value, performance.now());
    });

    $(room.state).players.onAdd((player, sessionId: string) => {
      const interpolator = new RemoteInterpolator();
      /**
       * Seeded with where the body already is (2026-08-26).
       *
       * Snapshots only ever came from `onChange`, so an entity that had not
       * moved since entering view had none at all, `sample()` returned null,
       * and it rendered at the origin. The dungeon mostly hides this — the
       * fly-through covers the first seconds and everything is moving by the
       * time it ends — but "hasn't moved yet" is a real state here too: a
       * squadmate holding station at the far end of a corridor enters view
       * with nothing to sample. Found via the arena, whose new countdown
       * makes it every fighter for five seconds.
       */
      interpolator.pushSnapshot({
        serverTime: room!.state.serverTime, x: player.x, z: player.z, facing: player.facing,
      });
      remoteInterpolators.set(sessionId, interpolator);

      // Jump (2026-10-16) — someone else's hop. The arc is drawn every frame
      // from the synced timestamp; this only starts the clip, and only for a
      // jump still in the air (not a stale value on first sight). The local
      // player already played it on the keypress.
      $(player).listen('jumpStartedAt', (value: number) => {
        if (sessionId === localSessionId || !isFreshJump(estimatedServerTimeNow(), value)) return;
        visuals.get(sessionId)?.playJump(JUMP_DURATION_MS);
      });

      // Registered immediately, before the model finishes loading — position/state
      // data must not be dropped just because the async load below is still in flight.
      $(player).onChange(() => {
        // combat-depth follow-up (2026-08-21) — reads `roomState.serverTime`
        // directly rather than any separately-tracked mirror of it: found live
        // as the cause of local-camera jerk during movement/combat, and the
        // same hazard applies to the render clock, which is why a snapshot is
        // never stamped from it. Colyseus fires every changed listener for
        // one patch only after the ENTIRE patch has been decoded into the
        // live state tree, but does not guarantee `listen('serverTime', ...)`
        // runs before a given entity's `onChange` in that same patch — so a
        // mirrored copy could still hold the PREVIOUS tick's value
        // when this fired, tagging the pushed snapshot with a stale
        // timestamp roughly one broadcast interval (50ms) off, at random,
        // tick to tick. `roomState.serverTime` is the live tree itself, so
        // it is always this patch's true value regardless of listener order.
        remoteInterpolators.get(sessionId)?.pushSnapshot({
          serverTime: roomState.serverTime, x: player.x, z: player.z, facing: player.facing,
        });
        visuals.get(sessionId)?.setState(player.state as EntityStateName, player.weaponClass as WeaponClass);
        // Through the scene's fixed lantern pool rather than a light of the
        // visual's own (2026-10-15, QA #9): lighting a lantern for the first
        // time used to add a PointLight, which recompiled every shader.
        dScene.setLanternHolder(sessionId, player.lanternLit ? visuals.get(sessionId)?.root ?? null : null);

        // "when I am being attacked, it is not obvious" — flash on any hp drop
        // for the local player specifically (a heal or the initial snapshot
        // must not trigger it).
        if (sessionId === localSessionId) {
          if (lastLocalHp !== null && player.hp < lastLocalHp) damageFlash.trigger();
          lastLocalHp = player.hp;
        }
      });

      dScene.addPlayer(player.characterClass as CharacterClassId, player.skinAssetKey || undefined,
        {
          weaponModel: player.equippedWeapon || undefined,
          offhandModel: player.equippedOffhand || undefined,
          // A comma-joined string on the wire; see `PlayerState.wornGear` on
          // why it is one field. The filter drops the empty string an unarmoured
          // delver sends, which would otherwise become a one-element array
          // holding nothing.
          wornGear: player.wornGear ? player.wornGear.split(',').filter(Boolean) : undefined,
        },
      ).then((visual) => {
        // The player may have already left while their model was loading (Edge Case #6's
        // sibling case: nothing render-breaking should happen from a slow load + a fast leave).
        if (disposed || !room?.state.players.has(sessionId)) { visual.dispose(); return; }
        visuals.set(sessionId, visual);
        visual.setState(player.state as EntityStateName, player.weaponClass as WeaponClass);
      }).catch((err) => {
        console.error('[dungeon-instance] failed to load character model', err);
        hud.logCombatEvent(`character load failed: ${(err as Error).message ?? err}`);
      });
    });

    $(room.state).players.onRemove((_player, sessionId: string) => {
      dScene.setLanternHolder(sessionId, null);
      visuals.get(sessionId)?.dispose();
      visuals.delete(sessionId);
      remoteInterpolators.delete(sessionId);
    });

    $(room.state).monsters.onAdd((monster, monsterId: string) => {
      const monsterInterpolator = new RemoteInterpolator();
      // Same seeding as the players above: a DORMANT skeleton has never moved,
      // so without this it renders at the origin until it wakes up.
      monsterInterpolator.pushSnapshot({
        serverTime: room!.state.serverTime, x: monster.x, z: monster.z, facing: monster.facing,
      });
      monsterInterpolators.set(monsterId, monsterInterpolator);
      const weaponClass = monsterRosterEntry(monster.archetype).weaponClass;
      /**
       * DORMANT -> AGGROED is not a state change — the monster is IDLE either
       * side of it — so the moment a skeleton noticed you had no animation and
       * it just started walking. Tracked here so the awaken clip can fire once,
       * on the edge, rather than every tick it is aggroed.
       */
      let lastAggro = monster.aggroState;

      // Same "register before the async load resolves" ordering as players
      // above, and the same roomState.serverTime fix (see that comment).
      $(monster).onChange(() => {
        monsterInterpolators.get(monsterId)?.pushSnapshot({
          serverTime: roomState.serverTime, x: monster.x, z: monster.z, facing: monster.facing,
        });
        monsterVisuals.get(monsterId)?.setState(monster.state as EntityStateName, weaponClass);
        if (monster.aggroState !== lastAggro) {
          if (lastAggro === 'DORMANT' && monster.aggroState === 'AGGROED') {
            monsterVisuals.get(monsterId)?.playAwaken();
          }
          lastAggro = monster.aggroState;
        }
        // `hpMax` is synced (2026-08-24). This used to record whatever HP the
        // monster had when `onAdd` fired and treat that as full — but `onAdd`
        // fires on entering this client's StateView, not on spawn, so anything
        // that walked into view wounded showed a full bar forever.
        monsterVisuals.get(monsterId)?.setHealthFraction(monster.hp / (monster.hpMax || 1));
      });

      dScene.addMonster(monster.archetype as MonsterArchetype).then((visual) => {
        if (disposed || !room?.state.monsters.has(monsterId)) { visual.dispose(); return; }
        monsterVisuals.set(monsterId, visual);
        visual.setState(monster.state as EntityStateName, weaponClass);
        visual.attachHealthBar(MONSTER_HEALTH_BAR_HEIGHT_M);
        visual.setHealthFraction(monster.hp / (monster.hpMax || 1));
        // Dark until the frame loop says otherwise — never one frame of a
        // labelled skeleton in the dark.
        visual.setTellsVisible(false);
      }).catch((err) => {
        console.error('[dungeon-instance] failed to load monster model', err);
        hud.logCombatEvent(`monster model load failed: ${(err as Error).message ?? err}`);
      });
    });

    $(room.state).monsters.onRemove((_monster, monsterId: string) => {
      monsterRevealedIds.delete(monsterId);
      monsterVisuals.get(monsterId)?.dispose();
      monsterVisuals.delete(monsterId);
      monsterInterpolators.delete(monsterId);
    });

    $(room.state).doors.onAdd((door, id: string) => {
      dScene.addDoorBeacon(id, door.x, door.z);
      dScene.updateDoorState(id, door.state as 'UNBARRED' | 'BARRED');
      $(door).onChange(() => dScene.updateDoorState(id, door.state as 'UNBARRED' | 'BARRED'));
    });
    $(room.state).doors.onRemove((_door, id: string) => dScene.removeDoorBeacon(id));

    $(room.state).portcullises.onAdd((p, id: string) => {
      dScene.addPortcullisBeacon(id, p.x, p.z);
      dScene.updatePortcullisState(id, p.state as 'SEALED' | 'CRANKING' | 'OPEN');
      $(p).onChange(() => dScene.updatePortcullisState(id, p.state as 'SEALED' | 'CRANKING' | 'OPEN'));
    });
    $(room.state).portcullises.onRemove((_p, id: string) => dScene.removePortcullisBeacon(id));

    $(room.state).chests.onAdd((chest, id: string) => {
      dScene.addChestBeacon(id, chest.x, chest.z);
      dScene.updateChestState(id, chest.state as 'CLOSED' | 'OPEN');
      $(chest).onChange(() => dScene.updateChestState(id, chest.state as 'CLOSED' | 'OPEN'));
    });
    $(room.state).chests.onRemove((_chest, id: string) => dScene.removeChestBeacon(id));

    $(room.state).braziers.onAdd((brazier, id: string) => {
      dScene.addBrazierVisual(id, brazier.x, brazier.z);
      dScene.updateBrazierState(id, brazier.state as 'LIT' | 'DOUSED');
      $(brazier).onChange(() => dScene.updateBrazierState(id, brazier.state as 'LIT' | 'DOUSED'));
    });
    $(room.state).braziers.onRemove((_brazier, id: string) => dScene.removeBrazierVisual(id));

    room.onMessage('combat.result', (msg: { attackerId: string; defenderId: string; outcome: string; damage?: number; isRiposte?: boolean; staggers?: boolean; died?: boolean }) => {
      const short = (id: string) => id.slice(0, 4);
      const dmg = msg.damage !== undefined ? ` (${msg.damage} dmg${msg.isRiposte ? ', riposte' : ''})` : '';
      hud.logCombatEvent(`${short(msg.attackerId)} -> ${short(msg.defenderId)}: ${msg.outcome}${dmg}`);

      // Combat-legibility slice (2026-08-24) — the number, on the body, where
      // the fight is. This payload already carried the exact damage; the only
      // place it went was a scrolling line in the top-left playtest overlay.
      const entity = room?.state.players.get(msg.defenderId) ?? room?.state.monsters.get(msg.defenderId);
      const floater = combatTextFor(msg, localSessionId ?? '', isSquadmate);
      if (floater && entity) damageNumbers.spawn(floater.text, floater.kind, entity.x, entity.z);

      // Implicit targeting: fighting something makes it the target frame's
      // subject, unless the player has explicitly chosen one with Tab. Most
      // players will never press Tab and still need to know what they are
      // hitting and how close it is to dead.
      // QA defect C (2026-10-16): only a monster YOU hit, and only while you
      // have no live target — the frame used to jump to every monster that
      // hit you. Through `setLocalTarget`, so the server learns it too.
      const current = localTargetId ? room?.state.monsters.get(localTargetId) : undefined;
      const implicit = pickImplicitTarget({
        attackerId: msg.attackerId,
        defenderId: msg.defenderId,
        selfId: localSessionId ?? '',
        currentId: localTargetId,
        // A non-monster target is a squadmate or yourself, chosen on purpose.
        currentLive: current ? current.state !== 'DEAD' : localTargetId !== '',
        isHostile: (id) => room?.state.monsters.has(id) ?? false,
      });
      if (implicit) setLocalTarget(implicit, false);

      /*
       * Every outcome makes a sound now, not only 'hit' (audio.md §3.2).
       * combat-resolver.ts has always resolved hit/parried/dodged/miss/blocked
       * plus staggers and isRiposte; until this call the other four were
       * silent, which meant "parry-into-stagger" -- named in the concept doc as
       * the core of the 30-second loop -- had no audio at all.
       *
       * Positioned on the DEFENDER, like the impact below: that is where the
       * event happens, and a parry is a thing that happens at the blade being
       * turned aside.
       */
      const struckEntity = room?.state.players.get(msg.defenderId) ?? room?.state.monsters.get(msg.defenderId);
      if (struckEntity) {
        audioEngine.playCombatOutcome({ x: struckEntity.x, z: struckEntity.z }, msg.outcome, {
          damage: msg.damage,
          isRiposte: msg.isRiposte,
          staggers: msg.staggers,
          // The entire bestiary is skeletal, so anything that is not a player
          // gets the bone impacts -- dry cracks rather than wet thuds.
          targetIsBone: !room?.state.players.has(msg.defenderId),
        });
      }

      // A passive dodge (2026-10-16) — the defender leans out of the blade's
      // path. Purely visual; the server never moved them.
      if (msg.outcome === 'dodged') {
        (visuals.get(msg.defenderId) ?? monsterVisuals.get(msg.defenderId))?.playDodgeSidestep();
      }

      if (msg.outcome === 'hit') {
        const defenderVisual = visuals.get(msg.defenderId) ?? monsterVisuals.get(msg.defenderId);
        defenderVisual?.playImpactVfx();
        // Game Feel Polish Phase 2c — only for hits that don't already get a
        // full replace-animation from the state machine (STAGGERED/DEAD).
        if (!msg.staggers && !msg.died) void defenderVisual?.playHitFlinch();
        // The impact itself is played by `playCombatOutcome` above, which
        // delegates to `playHitImpact` — calling it again here would stack two
        // impacts on one blow.
        const tookHit = msg.defenderId === localSessionId;
        const landedHit = msg.attackerId === localSessionId;
        if (tookHit || landedHit) {
          hitStopUntilMs = performance.now() + HIT_STOP_MS;
          dScene.shakeCamera(tookHit ? SHAKE_HIT_TAKEN_M : SHAKE_HIT_LANDED_M, SHAKE_DURATION_MS);
        }
      }
    });

    room.onMessage('world.changed', (msg: { objectType: string; objectId: string; newState: string }) => {
      hud.logCombatEvent(`world: ${msg.objectType} -> ${msg.newState}`);
    });

    /**
     * The squad roster, outside the interest-management gate.
     *
     * The party list used to be built by iterating `room.state.players`, which
     * only holds who the local client can currently SEE — so a squadmate in
     * the dark, or round a corner, disappeared from the panel entirely. See
     * `sendPartyStates` on the server for why this is a message rather than
     * un-gated schema.
     */
    room.onMessage('party.state', (msg: { members: PartyMemberMessage[] }) => {
      partyMembers = msg.members;
    });

    room.onMessage('ability.state', (msg: AbilityStateView) => {
      abilityState = msg;
      actionBar.update(msg, estimatedServerTimeNow());
    });

    // Handlers are live now, so it is safe to ask for the action bar. Requested
    // rather than pushed at join: see the server's `ability.request` note.
    requestAbilityState(room);
    // And the bag, so the weight readout can say "0.0 / 25kg" before the first
    // chest rather than after it.
    requestLootInventory(room);

    room.onMessage('input.rejected', (msg: { reason: string; state?: string; action?: string }) => {
      // A refused jump just lands early — no notice: the client already
      // mirrors the rules, so this is a race, and a "Still on cooldown" over
      // the action bar for a hop would be noise.
      if (msg.action === 'jump') { hud.logCombatEvent(`rejected: jump ${msg.reason}`); localJump.cancel(); return; }
      // The log line stays — it is the record, and it is what a developer
      // reads. The notice is what the player reads, in words, above the button
      // they just pressed. `abilityState.resourceKind` names their own bar.
      hud.logCombatEvent(`rejected: ${msg.reason}`);
      abilityNotice.show(msg.reason, msg.state, abilityState?.resourceKind);
      audioEngine.playUiReject();
      // One longer hint, once a run, for a player who keeps missing a target
      // (2026-10-16) — the short refusal names the problem, this the rule.
      if (TARGET_REFUSALS.has(msg.reason) && ++targetRefusals === 3) {
        abilityNotice.showText(touch
          ? t('Attacks need an enemy you can see, within reach. Tap Target to pick one, and watch the frame at the top for the distance.')
          : t('Attacks need an enemy you can see, within reach. Tab picks one, and the frame at the top shows the distance.'), 5_000);
      }
    });

    // A quest finishing mid-run used to be completely silent — core's
    // `trackAction` flips the row in Postgres and tells nobody. The server
    // now diffs the player's quests after each tracked action and sends this.
    room.onMessage('quest.completed', (msg: QuestCompleteNotice) => {
      hud.logCombatEvent(t('quest complete: {name}', { name: t(msg.name) }));
      questBanner.show(msg);
    });

    room.onMessage('loot.inventory', (msg: {
      items: { itemId: string; itemDefId: string; weightKg: number; insured: boolean }[];
      carriedWeightKg: number;
      carryCapacityKg?: number;
    }) => {
      lootPanel.update(msg.items, msg.carriedWeightKg, msg.carryCapacityKg);
      // The loot and insurance steps have nothing to point at until the bag
      // has something in it, so the bag is what unlocks them.
      if (msg.items.length > 0) signalTutorial('run:looted');
      if (msg.items.some((i) => i.insured)) signalTutorial('run:insured');
      if (lastLootItemCount !== -1 && msg.items.length > lastLootItemCount) audioEngine.playUiLoot();
      lastLootItemCount = msg.items.length;

      // What just went in, announced (2026-09-07). See `lastLootItemIds` on
      // why the first snapshot is a baseline rather than a pile of pickups.
      const seen = lastLootItemIds;
      const remainingKg = msg.carryCapacityKg === undefined
        ? undefined
        : Math.max(0, msg.carryCapacityKg - msg.carriedWeightKg);
      if (seen) {
        for (const item of msg.items) {
          if (seen.has(item.itemId)) continue;
          lootToasts.push({ itemDefId: item.itemDefId, weightKg: item.weightKg, remainingKg });
        }
      }
      lastLootItemIds = new Set(msg.items.map((i) => i.itemId));
    });

    room.onMessage('run.completed', (msg: RunEndInfo) => {
      const minutes = (msg.durationMs / 60_000).toFixed(1);
      hud.logCombatEvent(`RUN ${msg.outcome} — stratum ${msg.stratum}, ${minutes}min`);
      // The run-end stage. It stays up until the player dismisses it — the
      // server's own 3s disconnect no longer decides how long they get to
      // read it. See `ui/run-end-overlay.ts`.
      runEndOverlay.show(msg);
      // Nothing left to abandon once the run is settled, and a Menu button
      // floating over the summary would only offer to quit a run that is
      // already over.
      runMenu.setAvailable(false);
      // The summary is a screen full of buttons; give the cursor back.
      camera.releaseLook();
      // A level crossed by the completion bonus has had no in-world moment —
      // the world is already gone by the time it is written. The summary is the
      // only place it can be heard, so the fanfare plays here too.
      if (msg.leveledTo) audioEngine.playLevelUp();
      combatHud.setVisible(false); // the summary owns the screen now
      // And the route preview, if a run somehow ended during it — advice about
      // a run that is over.
      endFlyThrough();
      audioEngine.playRunEnd(msg.outcome);
    });

    // Multi-stratum amendment — the server sends this then calls
    // client.leave() on its own end; the reservation carries this player
    // straight into the next stratum's room, same as the initial hub→dungeon
    // handoff (dungeon-instance-client.ts's joinDungeonInstanceViaReservation).
    room.onMessage('stratum.descend', (msg: { roomId: string; reservation: SeatReservation; stratum: number }) => {
      hud.logCombatEvent(`descending to stratum ${msg.stratum}...`);
      descendReservation = msg.reservation;
    });

    room.onLeave(() => {
      hud.logCombatEvent('disconnected from match');
      if (descendReservation) {
        const next = descendReservation;
        currentTeardown?.();
        mountDungeonInstance(root, _user, _onLogout, next, onMatchEnd);
        return;
      }
      // The run-end summary is showing: the player has not finished with it.
      // Leaving the socket and leaving the *stage* are different events, and
      // conflating them is what made death a three-second flash. The overlay's
      // own button calls onMatchEnd when they are ready.
      if (runEndOverlay.isShowing) return;
      // Only fire for a genuine server-side end (run completed, kicked, etc.) — an
      // external caller tearing this scene down itself (e.g. logout) already set
      // `disposed` before calling room.leave(), so it must not also trigger onMatchEnd.
      if (!disposed) onMatchEnd?.();
    });

    // MMO combat integration (2026-08-24) — the boss's phase script announcing
    // itself. Surfaced as a log line and an audio sting: a phase change is
    // information about a fight in progress, which concept doc §4 wants
    // arriving from the world rather than from a boss-health UI.
    room.onMessage('boss.phase', (msg: { monsterId: string; phaseId: string; phaseName: string }) => {
      hud.logCombatEvent(t('the Warden enters {phase}', { phase: t(msg.phaseName) }));
      audioEngine.playRunEnd('DIED'); // reuses the existing low sting; no new asset
      dScene.shakeCamera(SHAKE_HIT_TAKEN_M, SHAKE_DURATION_MS);
    });

    /**
     * Visualizing abilities (2026-08-26) — user request: "we also need to
     * visualize the abilities and spells in-game."
     *
     * One press produces four separate things, in the order the player reads
     * them: the caster moves, the effect blooms, whoever was hit reacts, and
     * it makes a sound where it happened. Before this the whole of it was one
     * yellow ring at the caster's feet — identical for all fourteen abilities
     * — and an ability's damage produced no reaction on its victim at all,
     * because ability damage does not go through `combat.result`.
     */
    room.onMessage('ability.used', (msg: AbilityUsedMessage) => {
      hud.logCombatEvent(`ability: ${msg.playerId.slice(0, 4)} used ${msg.abilityId}`);
      const visual = abilityVisual(msg.abilityId);
      const caster = room?.state.players.get(msg.playerId);

      // The caster's own motion. Timed to the GCD so the clip is out of the
      // way before the next press in a rotation.
      visuals.get(msg.playerId)?.playAbilityAnimation(
        ABILITY_FX_INTENT[visual.fx], ABILITY_ANIMATION_SECONDS,
      );

      // The caster's own effect — a ward closing over them, a ring leaving
      // them at the radius it actually swept, a bolt on its way. Drawn from
      // what the server said it did, so a talent that scales a radius or a
      // duration cannot make the drawing disagree with the fight.
      if (CASTER_FX.has(visual.fx)) {
        abilityFx.spawn({
          kind: visual.fx,
          colorHex: visual.colorHex,
          origin: anchorFor(msg.playerId),
          target: msg.targetId ? anchorFor(msg.targetId) : undefined,
          radiusM: msg.radiusM,
          durationMs: fxDurationFor(visual.fx, msg.durationMs),
        });
      }
      if (caster) audioEngine.playAbilityCast({ x: caster.x, z: caster.z }, visual.fx);

      // Everything the ability landed on, one effect per body rather than one
      // on the chosen subject: a Cleave that caught four skeletons flashes on
      // four of them. The caster's ring says how far the ability reached; the
      // flashes say who was actually in it, which is the part that decides
      // whether the player presses it again.
      for (const hit of msg.hits ?? []) {
        const onHit = HIT_FX[visual.fx];
        if (onHit) {
          abilityFx.spawn({
            kind: onHit,
            colorHex: visual.colorHex,
            origin: anchorFor(hit.id),
            durationMs: fxDurationFor(onHit, msg.durationMs),
          });
        }
        if (hit.damage === undefined) continue;
        // The same reaction a weapon hit gets — number, burst, flinch, sound,
        // and a shake when it is you. Ability damage bypasses `combat.result`
        // (it has no parry/riposte/stagger to report), so until now an
        // Eviscerate for 34 produced no reaction on its victim whatsoever:
        // the health bar moved and nothing else did.
        const body = visuals.get(hit.id) ?? monsterVisuals.get(hit.id);
        const entity = room?.state.players.get(hit.id) ?? room?.state.monsters.get(hit.id);
        if (entity) {
          damageNumbers.spawn(String(hit.damage), hit.id === localSessionId ? 'taken' : 'dealt', entity.x, entity.z);
          audioEngine.playHitImpact({ x: entity.x, z: entity.z }, hit.damage, !room?.state.players.has(hit.id));
        }
        body?.playImpactVfx();
        // Same rule the weapon path uses: no additive flinch on a body that
        // is already playing a full-body death.
        if (!hit.died) void body?.playHitFlinch();
        // The screen flash is not repeated here — `lastLocalHp` already
        // triggers it on any drop in the local player's health, whatever
        // caused it.
        if (hit.id === localSessionId) dScene.shakeCamera(SHAKE_HIT_TAKEN_M, SHAKE_DURATION_MS);
      }

      // Combat-legibility slice — a green number over whoever was healed. The
      // server sends the amount actually restored, which is not the ability's
      // magnitude: dampening and the overheal clamp both apply first.
      if (msg.restoredHp && msg.restoredHp > 0 && msg.targetId) {
        const healed = room?.state.players.get(msg.targetId);
        if (healed) damageNumbers.spawn(`+${msg.restoredHp}`, 'heal', healed.x, healed.z);
      }
    });

    /**
     * Quick-use consumables (2026-08-25) — a channel completed.
     *
     * A separate message from `ability.used` rather than a reused one: that
     * payload's handler indexes abilities by id, and a consumable is
     * deliberately not in the ability table. Reusing it is exactly the trap
     * the comment above records.
     */
    /**
     * A channel has *started*, and it is an item rather than a spell — see the
     * server's `announceConsumableChannel`. Arrives just before the CASTING
     * state does, which is what lets the pose be right for the whole 1.5s.
     */
    /**
     * A premium emote. Purely cosmetic and interruptible — any real state
     * change crossfades away from it, so getting hit stops the dance.
     */
    room.onMessage('emote.played', (msg: { playerId: string; clipName: string }) => {
      void visuals.get(msg.playerId)?.playEmote(msg.clipName);
    });

    /**
     * character-creation.md (amended 2026-08-25) — somebody levelled.
     *
     * The pillar plays on whoever levelled, squadmate or not: it is their
     * body the light belongs to, and a party member glowing for no visible
     * reason would be worse than not showing it. The banner and the fanfare
     * are the local player's alone — it is news about you, and a squadmate's
     * level is worth a line in the log, not the middle of your screen
     * mid-fight.
     */
    room.onMessage('character.levelup', (msg: {
      sessionId: string; name?: string; level: number; xpIntoLevel?: number; xpForLevel?: number;
    }) => {
      visuals.get(msg.sessionId)?.playLevelUpVfx();
      if (msg.sessionId === localSessionId) {
        const progress = msg.xpIntoLevel !== undefined && msg.xpForLevel !== undefined
          ? { into: msg.xpIntoLevel, needed: msg.xpForLevel }
          : undefined;
        levelUpBanner.show(msg.level, progress);
        audioEngine.playLevelUp();
        hud.logCombatEvent(`LEVEL UP — level ${msg.level}`);
      } else {
        hud.logCombatEvent(`${msg.name ?? msg.sessionId.slice(0, 4)} reached level ${msg.level}`);
      }
    });

    /**
     * Reviving a downed squadmate (2026-08-25).
     *
     * The channel is broadcast so everyone sees the reviver kneel — the
     * CASTING state already animates, so this only supplies the *timing* the
     * progress line needs, which is not derivable from the synced state.
     */
    room.onMessage('party.revive_channel', (msg: { reviverId: string; targetId: string; endsAt: number }) => {
      // The body's own view of it: "coming" becomes "lifting you", which is
      // the moment the countdown stops being the only thing on the screen.
      if (msg.targetId === localSessionId) {
        const who = room?.state.players.get(msg.reviverId);
        downedOverlay.setRescuer(who?.nickname ?? t('A squadmate'), true);
      }
      if (msg.reviverId !== localSessionId) return;
      activeReviveTargetId = msg.targetId;
      reviveStartedAt = estimatedServerTimeNow();
      reviveEndsAt = msg.endsAt;
    });

    /**
     * The downed window (amendment 2026-09-01). The lethal hit no longer ends
     * the run, so this — not `run.completed` — is what a killed player now
     * sees first. `run.completed` still arrives if nobody reaches them, and
     * its own handler takes the screen from there.
     */
    room.onMessage('party.downed', (msg: {
      sessionId: string; nickname?: string; windowMs: number; serverTs: number; rescuerAvailable?: boolean;
    }) => {
      const isMe = msg.sessionId === localSessionId;
      if (isMe) {
        // Server time to local time through the same offset every other clock
        // in this scene uses, so the countdown cannot drift against the rule.
        const endsAtLocal = performance.now() + Math.max(0, (msg.serverTs + msg.windowMs) - estimatedServerTimeNow());
        // The server's answer, not ours. Counting live squadmates here read
        // the client's own player map, which is interest-managed — a party two
        // rooms away is not in this client's StateView at all, so the screen
        // said "Nobody is left to reach you" while the server was holding the
        // window open for them. Observed live on 2026-09-02. The local scan
        // survives only as the fallback for an older server.
        let squadmateAlive = msg.rescuerAvailable ?? false;
        if (msg.rescuerAvailable === undefined) {
          const me = room?.state.players.get(localSessionId ?? '');
          for (const [id, p] of room?.state.players.entries() ?? []) {
            if (id === localSessionId || !me) continue;
            if (p.squadId && p.squadId === me.squadId && p.state !== 'DEAD' && p.connected) { squadmateAlive = true; break; }
          }
        }
        // No possible rescuer means no window — the server has already
        // finalised the run and the summary is on its way. A countdown here
        // would be a clock counting toward nothing, which is a promise that
        // waiting is the thing to do. See the server's own gate on this.
        if (!squadmateAlive) return;
        downedOverlay.show(endsAtLocal);
        camera.releaseLook();
        hud.logCombatEvent('YOU ARE DOWN — hold on');
      } else if (isSquadmate(msg.sessionId)) {
        // Both halves of this line used to come from `room.state.players`,
        // which is interest-managed — so a squadmate who went down anywhere
        // out of sight produced NO message at all, and the twenty seconds you
        // had to reach them passed without the game ever mentioning it. The
        // roster and the payload are the two things here that are not gated.
        const name = msg.nickname
          || partyMembers.find((m) => m.sessionId === msg.sessionId)?.nickname
          || t('A squadmate');
        hud.logCombatEvent(`${name} is DOWN — hold E over them to revive`);
        // The instruction is the player's, not the log's (2026-10-16).
        abilityNotice.showText(touch
          ? t('{name} is down — hold Use over them to revive', { name })
          : t('{name} is down — hold E over them to revive', { name }), 4_000);
        // A sound, because this is precisely the case where the player is
        // looking somewhere else: the body may be in another room. The low
        // note `playRunEnd('DIED')` already carries is the right one — a
        // squadmate's run is what is about to end.
        audioEngine.playRunEnd('DIED');
      }
    });

    /**
     * Who is on their way for you, sent only to the player on the floor
     * (2026-09-02). Bots revive now and are allowed to fail, so the downed
     * screen reports what is actually happening instead of a standing promise
     * — see `DownedOverlay.setRescuer`.
     */
    room.onMessage('party.rescuer', (msg: { reviverId: string; nickname: string }) => {
      downedOverlay.setRescuer(msg.nickname || null, false);
    });

    /**
     * Somebody got picked up. For the revived player this is the moment the
     * downed screen comes off and the run simply continues — their loot and
     * their run were never touched, because the completion is what the window
     * defers.
     */
    room.onMessage('party.revived', (msg: { sessionId: string; byId: string; hp: number }) => {
      const target = room?.state.players.get(msg.sessionId);
      if (msg.sessionId === localSessionId) {
        downedOverlay.hide();
        combatHud.setVisible(true);
        hud.logCombatEvent('BACK ON YOUR FEET');
      } else if (target) {
        hud.logCombatEvent(`${target.nickname} is back up`);
      }
      if (msg.byId === localSessionId) {
        activeReviveTargetId = null;
        reviveEndsAt = 0;
      }
    });

    room.onMessage('party.revive_cancelled', () => {
      // Moving or taking a hit cancels it server-side, so this is usually the
      // first the player hears of it. Said out loud rather than left to the
      // progress line vanishing.
      hud.logCombatEvent('revive interrupted');
      abilityNotice.showText(t('Revive interrupted'));
      activeReviveTargetId = null;
      reviveEndsAt = 0;
    });

    room.onMessage('party.revived', (msg: { sessionId: string; byId: string; hp: number }) => {
      const name = room?.state.players.get(msg.sessionId)?.nickname ?? msg.sessionId.slice(0, 4);
      hud.logCombatEvent(`${name} is back on their feet (${Math.round(msg.hp)} hp)`);
      abilityNotice.showText(msg.sessionId === localSessionId
        ? t('Back on your feet')
        : t('{name} is back on their feet', { name }));
      // The same motes a heal draws, in gold rather than green: getting a
      // squadmate off the floor is the same kind of news as healing them, and
      // an effect invented for one event would be a third thing to maintain.
      abilityFx.spawn({
        kind: 'BLESSING', colorHex: 0xffd98a, origin: anchorFor(msg.sessionId),
        durationMs: ABILITY_FX_DURATION_MS.BLESSING,
      });
      if (msg.byId === localSessionId) {
        activeReviveTargetId = null;
        reviveEndsAt = 0;
        audioEngine.playUiLoot(); // the same bright two-note confirmation a pickup gets
      }
    });

    room.onMessage('consumable.channel', (msg: { playerId: string }) => {
      visuals.get(msg.playerId)?.setChannelIntent('USE_ITEM');
    });

    room.onMessage('consumable.used', (msg: { playerId: string; defId: string }) => {
      hud.logCombatEvent(`used ${msg.defId}`);
      abilityFx.spawn({
        kind: 'BLESSING', colorHex: 0x9fe8b0, origin: anchorFor(msg.playerId),
        durationMs: ABILITY_FX_DURATION_MS.BLESSING,
      });
      if (msg.playerId === localSessionId) audioEngine.playUiLoot();
    });

    /**
     * The Salve ticking. One float per second, batched server-side — a float
     * per sim tick would be ~20 numbers a second stacked on one head.
     *
     * Positions are read off synced state rather than sent, because the
     * drinker has almost certainly moved since the channel finished.
     */
    room.onMessage('consumable.healed', (msg: { playerId: string; hp: number }) => {
      if (msg.hp <= 0) return;
      const healed = room?.state.players.get(msg.playerId);
      if (healed) damageNumbers.spawn(`+${msg.hp}`, 'heal', healed.x, healed.z);
    });

    // game-concept.md §13a — Space (jump, since 2026-10-16) doubles as the
    // fly-through skip key rather than a new listener; every other input is
    // simply swallowed while the fly-through plays.
    input.onJump = () => {
      if (flyThroughActive) { endFlyThrough(); return; }
      jumpLocal();
    };
    input.onAttack = (dir) => { if (!flyThroughActive && room) { signalTutorial('run:attacked'); flushAim(); predictLocalState('ATTACK_WINDUP', dir); sendAttack(room, dir); } };
    input.onParry = () => { if (!flyThroughActive && room) { flushAim(); predictLocalState('PARRY_WINDOW'); sendParry(room); } };
    input.onFeint = () => { if (!flyThroughActive && room) { predictLocalState('FEINT_RECOVERY'); sendFeint(room); } };
    input.onLanternToggle = () => { if (!flyThroughActive && room) sendLanternToggle(room); };
    input.onEmoteMenu = () => {
      if (flyThroughActive) return;
      // A captured pointer has no cursor to click a menu with — hand it back
      // when the picker opens; clicking the world takes it again.
      camera.releaseLook();
      emoteMenu.toggle();
    };
    emoteMenu.onPick = (emoteId) => { if (room) sendEmote(room, emoteId); };
    input.onAbility = () => { if (!flyThroughActive && room) { signalTutorial('run:ability'); sendAbility(room); } };
    // MMO combat integration (2026-08-24) — action bar + soft target.
    input.onAbilitySlot = (slot) => {
      if (flyThroughActive || !room) return;
      signalTutorial('run:ability');
      acquireTargetForAbility(slot);
      sendAbilitySlot(room, slot);
    };
    input.onCycleTarget = () => { if (!flyThroughActive && room) cycleTarget(); };
    // Ally targeting (2026-10-16): F1 is you, F2–F4 the party frames top to
    // bottom; clicking or tapping a frame does the same.
    input.onSelectPartyMember = (index) => { if (!flyThroughActive) selectPartyMember(index); };
    combatHud.onSelectMember = (sessionId) => {
      if (flyThroughActive || !room) return;
      setLocalTarget(sessionId, true);
      signalTutorial('run:targeted');
    };
    actionBar.onActivate = (slot) => {
      if (flyThroughActive || !room) return;
      signalTutorial('run:ability');
      acquireTargetForAbility(slot);
      sendAbilitySlot(room, slot);
    };
    input.onSprintChange = (active) => { if (!flyThroughActive && room) sendSprint(room, active); };
    input.onCrouchChange = (active) => { if (!flyThroughActive && room) sendCrouch(room, active); };
    input.onInteractStart = () => { if (!flyThroughActive) handleInteractStart(); };
    input.onInteractEnd = () => { if (!flyThroughActive) handleInteractEnd(); };
    input.onDescend = () => { if (!flyThroughActive) handleDescendPress(); };
    input.onToggleInventory = () => {
      if (flyThroughActive) return;
      lootPanel.toggle();
      // Opening the bag hands the pointer back, the same way the emote picker
      // and the run-end screen do. Every row in that list has an Insure and a
      // Drop button on it, and a panel full of buttons you cannot click
      // because the cursor is captured for mouse-look is a panel that does
      // not open. Closing it does not re-capture: the "Click to look around"
      // prompt already owns getting the camera back, and stealing the pointer
      // on a keypress the player used to *dismiss* something would be the
      // camera grabbing at them.
      if (lootPanel.isExpanded) camera.releaseLook();
    };

    // Thumbs, wired to the same verbs the keys and the mouse use. Look goes
    // straight to applyLookDelta — the method Pointer Lock was already
    // driving — because a phone has no pointer to lock and the maths is
    // identical either way.
    touch?.start({
      onCycleTarget: () => { if (!flyThroughActive && room) cycleTarget(); },
      onLook: (dx, dy) => { if (!flyThroughActive) camera.applyLookDelta(dx, dy); },
      onAttack: () => {
        if (flyThroughActive) { endFlyThrough(); return; }
        if (room) { signalTutorial('run:attacked'); flushAim(); predictLocalState('ATTACK_WINDUP', 'OVERHEAD'); sendAttack(room, 'OVERHEAD'); }
      },
      onBlockChange: (active) => {
        if (flyThroughActive || !room) return;
        if (active) { flushAim(); predictLocalState('BLOCKING'); }
        sendBlock(room, active);
      },
      onJump: () => {
        if (flyThroughActive) { endFlyThrough(); return; }
        jumpLocal();
      },
      onInteractStart: () => { if (!flyThroughActive) handleInteractStart(); },
      onInteractEnd: () => { if (!flyThroughActive) handleInteractEnd(); },
    });
  })().catch((err) => {
    console.error('[dungeon-instance] failed to join room', err);
    hud.logCombatEvent(`connection failed: ${(err as Error).message ?? err}`);
  });

  let lastFrameTime = performance.now();
  /** Last sprint state actually sent for the stick, so the edge fires once. */
  let touchSprintSent = false;
  let hitStopUntilMs = 0;
  let rafHandle = 0;
  const frameTimer = new FrameTimer();
  // ~60 draws a second on a high-refresh panel (QA #9) — see sim/render-pacer.ts.
  // `__tallowUncapped` is a dev-only switch so headless perf runs can measure
  // raw throughput instead of the cap.
  const pacer = new RenderPacer(!(import.meta.env.DEV && (window as unknown as { __tallowUncapped?: boolean }).__tallowUncapped));

  function frame(): void {
    const now = performance.now();
    // A skipped callback does nothing at all — the next drawn frame's delta
    // simply covers both, which every consumer below already handles.
    if (!pacer.shouldRender(now)) { rafHandle = requestAnimationFrame(frame); return; }
    const rawDeltaMs = now - lastFrameTime;
    const dtSeconds = Math.min(0.1, rawDeltaMs / 1000);
    lastFrameTime = now;
    // Game Feel Polish Phase 5 — measured from the RAW delta, not the clamped
    // dtSeconds above: the clamp exists to keep simulation stable across a
    // tab-switch stall, and reusing it here would quietly hide the very
    // hitches this instrument is meant to surface.
    frameTimer.push(rawDeltaMs);

    // The stick, read once a frame and handed to the one thing that owns a
    // move vector. Sprint is an edge rather than a level because the server is
    // told about it in messages, not in the movement packet — so crossing the
    // ring has to look exactly like pressing Shift.
    if (touch) {
      input.touchDirection = touch.direction;
      const sprinting = touch.sprintHeld;
      if (sprinting !== touchSprintSent) {
        touchSprintSent = sprinting;
        input.onSprintChange?.(sprinting);
      }
    }

    const localPlayerLive = room && localSessionId ? room.state.players.get(localSessionId) : undefined;
    const hasMovementInput = input.direction.x !== 0 || input.direction.z !== 0;
    // Core Rule #2 — any movement key press skips the fly-through immediately, same as a click/tap or the Skip button.
    if (flyThroughActive && hasMovementInput) endFlyThrough();
    // camera-and-controls.md amendment (2026-08-25, fifth) — mouse-look. This
    // no longer takes `facing` at all: yaw and pitch are inputs, applied the
    // instant the mouse moves, and all `update` has left to do is ease the
    // shoulder-swap blend. `facing` is downstream of the camera now (the aim
    // send below), which is what retired four amendments' worth of machinery
    // for keeping the old camera-follows-facing loop from spinning.
    camera.update(dtSeconds);

    // Point the camera along the spawn facing, once. Nothing else ever reads
    // `facing` into the camera, so without this every delver would begin a run
    // looking due north whatever direction they were spawned pointing.
    if (!cameraAligned && localPlayerLive) {
      camera.alignTo(localPlayerLive.facing);
      cameraAligned = true;
    }

    // The world direction of a held key changes as the mouse turns, so this
    // still can't be an exact-equality check on the raw input — but the churn
    // is now the player's own hand rather than a yaw converging on a target,
    // which is what the old comment here was about. The 20Hz cap stays: it is
    // what keeps a high-refresh display from emitting past MOVE_PER_SEC (60)
    // during a fast turn, same spirit as this project's other broadcast-rate
    // knobs (e.g. NET.BROADCAST_HZ) rather than a per-frame send.
    const MOVE_SEND_INTERVAL_MS = 50; // 20Hz
    const DIRECTION_SEND_EPSILON = 0.001;
    const dir = camera.worldDirection(input.direction);
    const directionChanged = Math.abs(dir.x - lastSentDirection.x) > DIRECTION_SEND_EPSILON
      || Math.abs(dir.z - lastSentDirection.z) > DIRECTION_SEND_EPSILON;
    if (!flyThroughActive && room && directionChanged && now - lastMoveSentAt >= MOVE_SEND_INTERVAL_MS) {
      lastSentDirection = dir;
      lastMoveSentAt = now;
      // A direction change that is not back to standing still: the player has
      // walked. Cheaper and more honest than watching key events, which fire
      // for keys the camera swallowed.
      if (dir.x !== 0 || dir.z !== 0) signalTutorial('run:moved');
      sendMove(room, dir);
    }

    // Aim, on the same 20Hz budget and for the same reason. Sent whether or not
    // the player is moving: turning on the spot to face something changes
    // nothing about the movement direction, so `input.move` would never carry
    // it, and a swing would resolve against a facing from before the turn.
    // The epsilon is well under one mouse pixel's worth of yaw, so a still
    // hand sends nothing at all.
    const AIM_SEND_INTERVAL_MS = 50; // 20Hz, matching the move cadence
    const AIM_SEND_EPSILON = 0.001;
    const aimChanged = lastSentAimYaw === null
      || Math.abs(normalizeAngle(camera.viewYaw - lastSentAimYaw)) > AIM_SEND_EPSILON;
    if (!flyThroughActive && room && aimChanged && now - lastAimSentAt >= AIM_SEND_INTERVAL_MS) {
      lastSentAimYaw = camera.viewYaw;
      lastAimSentAt = now;
      sendAim(room, camera.viewYaw);
    }

    // The gate you are still holding E on may have just finished opening.
    // Checked before the HUD block below so the extract goes out on the same
    // frame the bar fills, rather than one frame after the line changes.
    if (!flyThroughActive) pollCrankedGateOpened();
    // The fly-through drives its own camera over a dungeon the player is not
    // standing in yet; a prompt from wherever they will spawn would be an
    // instruction about somewhere they cannot act.
    else interactPrompt.hide();

    if (!flyThroughActive && room && localSessionId) {
      const localPlayer = room.state.players.get(localSessionId);
      if (localPlayer) {
        let extraHudLine: string | undefined;
        if (activeReviveTargetId) {
          // Before `party.revive_channel` lands there is no server timing yet,
          // so the line says what is happening without claiming a percentage
          // it cannot know.
          const name = room.state.players.get(activeReviveTargetId)?.nickname ?? 'squadmate';
          if (reviveEndsAt > reviveStartedAt) {
            const frac = (estimatedServerTimeNow() - reviveStartedAt) / (reviveEndsAt - reviveStartedAt);
            extraHudLine = `reviving ${name}: ${(Math.min(1, Math.max(0, frac)) * 100).toFixed(0)}%`;
          } else {
            extraHudLine = `reviving ${name}...`;
          }
        } else if (activeCrankTargetId) {
          const p = room.state.portcullises.get(activeCrankTargetId);
          if (p) {
            const frac = crankProgressFraction(
              { checkpointProgressMs: p.checkpointProgressMs, checkpointAt: p.checkpointAt, activeCrankersCount: p.activeCrankers.length },
              estimatedServerTimeNow(),
            );
            extraHudLine = `cranking: ${(frac * 100).toFixed(0)}%`;
          }
        }

        /**
         * The prompt. Without it a body on the floor is scenery: nothing in
         * the game says a downed squadmate can be picked up, and a mechanic
         * nobody knows about is a mechanic that does not exist.
         *
         * Deliberately driven by `findNearestInteractable()` — the same call
         * the E key itself makes — so the prompt can only ever promise what
         * pressing E will actually do. Offering "revive" while a nearer
         * chest would win the keypress is worse than saying nothing.
         *
         * Moved out of `extraHudLine` on 2026-09-07 and into its own panel.
         * These lines were always written as player-facing copy — the comment
         * above says so — and for as long as they went to `updateStats`'s
         * `extra` argument they rendered inside the F1 block, which is
         * `hidden` until a developer opens it. Nobody playing this game has
         * ever seen one. The gate is where that finally showed up as a bug
         * report, but the revive line had the same problem since the day it
         * was written.
         */
        const promptLines: string[] = [];
        const nearestInteractable = findNearestInteractable();
        if (canLeaveWaves()) {
          // Named in full rather than "press E to extract": the whole decision
          // this mode is built on is what you are choosing to walk away with.
          promptLines.push(t('{press} to leave with what you are carrying', { press: pressKey() }));
        } else if (activeReviveTargetId || activeCrankTargetId) {
          // Mid-action: the progress line above is already saying what is
          // happening, and a second instruction under it would compete with it.
        } else if (nearestInteractable?.kind === 'downed') {
          promptLines.push(t('{hold} to revive {name}', { hold: holdKey(), name: nearestInteractable.label ?? t('squadmate') }));
        } else if (nearestInteractable?.kind === 'corpse') {
          promptLines.push(t('{press} to search the body', { press: pressKey() }));
        } else if (nearestInteractable?.kind === 'portcullis') {
          // An open gate offers two different endings to the run — see
          // `ui/run-objective.ts`. Both get a line.
          promptLines.push(...gatePromptLines(currentStratum, nearestInteractable.state));
        } else if (nearestInteractable?.kind === 'chest' && nearestInteractable.state !== 'OPEN') {
          // Only a shut one. An emptied chest stays an interactable so the
          // beacon can recolour, and offering to open it again would be the
          // same broken promise the gate was making.
          promptLines.push(t('{press} to open the chest', { press: pressKey() }));
        } else if (nearestInteractable?.kind === 'brazier') {
          promptLines.push(brazierPromptLine(nearestInteractable.state));
        }
        interactPrompt.show(promptLinesForInput(promptLines, touch !== null));

        /**
         * design/gdd/waves.md — the wave counter and the lull clock.
         *
         * Written into the objective plaque rather than given a banner of its
         * own, because it answers the same question the plaque already exists
         * to answer: what am I doing right now. In a delve `wavesPhase` is
         * `''` for the life of the room and this never runs.
         *
         * Throttled to twice a second. The number it shows only changes at 1Hz
         * and rewriting a text node every frame is work nobody can see.
         */
        if (room.state.wavesPhase && now - lastWavesLineAt >= 500) {
          lastWavesLineAt = now;
          const wave = room.state.wavesWave;
          if (room.state.wavesPhase === 'LULL') {
            const left = Math.max(0, Math.ceil((room.state.wavesPhaseEndsAt - room.state.serverTime) / 1000));
            hud.setObjective(wave === 0
              ? t('Wave 1 in {s}s. Hold this hall — what you kill drops loot, and it is only yours if you walk out with it.', { s: left })
              : t('Wave {wave} cleared. The stair is open for {s}s: leave with what you are carrying, or hold for wave {next}.', { wave, s: left, next: wave + 1 }));
          } else {
            hud.setObjective(t('Wave {wave}. The stair is shut until it is cleared.', { wave }));
          }
        }
        hud.updateStats(
          {
            pingMs: clockSync?.rtt ?? 0, state: localPlayer.state as EntityStateName, hp: localPlayer.hp, stamina: localPlayer.stamina,
            fps: frameTimer.fps, frameMs: frameTimer.meanMs, worstFrameMs: frameTimer.worstMs,
          },
          extraHudLine,
        );

        // ── The real HUD (combat-legibility slice, 2026-08-24) ───────────
        //
        // Health and stamina previously existed on screen only as a line of
        // monospace text inside the playtest overlay, and the resource bar
        // lived across the screen at the action bar. This is the panel a
        // player is actually meant to read.
        combatHud.setVitals({
          // The player's OWN maxima, off the synced state. Hardcoding the base
          // constants here is what produced "110 / 100": the knight's
          // `fortitude` talent adds 10 HP a rank, so 110 was the correct HP
          // over the wrong denominator.
          hp: localPlayer.hp,
          hpMax: localPlayer.hpMax,
          stamina: localPlayer.stamina,
          staminaMax: localPlayer.staminaMax,
          resource: abilityState?.resource ?? 0,
          resourceMax: abilityState?.resourceMax ?? 0,
          resourceKind: abilityState?.resourceKind ?? '',
          characterClass: localPlayer.characterClass as CharacterClassId,
          nickname: localPlayer.nickname,
        });

        // The party list. Also the first thing in the game that distinguishes
        // a squadmate from a rival — friendly fire and squad-only healing were
        // both enforced invisibly before this.
        const party: HudPartyMember[] = partyMembers.map((member) => ({
          sessionId: member.sessionId,
          nickname: member.nickname,
          characterClass: member.characterClass as CharacterClassId,
          role: rosterEntry(member.characterClass).role,
          hp: member.hp,
          hpMax: member.hpMax,
          isDead: member.dead,
          // Seconds left to reach them, or null once the body is past saving.
          // Counted down against the render clock rather than read off the
          // roster, so it ticks smoothly between the 4Hz updates.
          reviveSecondsLeft: member.revivable
            ? Math.max(0, Math.ceil((member.reviveEndsAt - estimatedServerTimeNow()) / 1000))
            : null,
          isLocal: false,
          // Computed here rather than sent, so it tracks the local player's own
          // predicted position between the 4Hz roster updates.
          distanceM: Math.hypot(member.x - localPlayer.x, member.z - localPlayer.z),
        }));
        // Sorted by role, not by join order, so the healer is always in the
        // same place — a list that reshuffles is a list you have to read.
        const ROLE_ORDER: Record<string, number> = { TANK: 0, HEALER: 1, DPS: 2 };
        party.sort((a, b) => (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) || a.sessionId.localeCompare(b.sessionId));
        partyFrameOrder = party.map((m) => m.sessionId);
        for (const member of party) member.isTargeted = member.sessionId === localTargetId;
        combatHud.setParty(party);

        // The target frame. `localTargetId` is set by Tab, F1–F4, a party
        // frame, or implicitly by whatever the player last traded blows with.
        // 2026-10-16: squadmates and yourself can be the subject now (heals),
        // an unrevealed monster never is, and "out of range" is said here
        // before a press is refused — against the longest reach of the
        // abilities that could act on it.
        const targetMonster = localTargetId ? room.state.monsters.get(localTargetId) : undefined;
        const targetAlly = localTargetId ? partyMembers.find((m) => m.sessionId === localTargetId) : undefined;
        const reachOf = (kind: 'ENEMY' | 'ALLY'): number => {
          const ranges = (abilityState?.abilities ?? [])
            .filter((a) => a.targeting === kind && a.rangeM !== undefined)
            .map((a) => a.rangeM as number);
          return ranges.length > 0 ? Math.max(...ranges) : Infinity;
        };
        if (targetMonster && targetMonster.state !== 'DEAD' && monsterRevealedIds.has(localTargetId)) {
          const distanceM = Math.hypot(targetMonster.x - localPlayer.x, targetMonster.z - localPlayer.z);
          combatHud.setTarget({
            name: MONSTER_DISPLAY_NAME[targetMonster.archetype as MonsterArchetype]
              ? t(MONSTER_DISPLAY_NAME[targetMonster.archetype as MonsterArchetype])
              : targetMonster.archetype,
            hp: targetMonster.hp,
            hpMax: targetMonster.hpMax,
            distanceM,
            isHostile: true,
            outOfRange: distanceM > reachOf('ENEMY'),
          });
          const body = monsterVisuals.get(localTargetId)?.root.position;
          targetRing.update(body ?? { x: targetMonster.x, y: 0, z: targetMonster.z }, true, dtSeconds);
        } else if (localTargetId && localTargetId === localSessionId) {
          combatHud.setTarget({
            name: localPlayer.nickname || t('You'),
            hp: localPlayer.hp,
            hpMax: localPlayer.hpMax,
            distanceM: 0,
            isHostile: false,
          });
          targetRing.update(visuals.get(localSessionId)?.root.position ?? null, false, dtSeconds);
        } else if (targetAlly && !targetAlly.dead) {
          const distanceM = Math.hypot(targetAlly.x - localPlayer.x, targetAlly.z - localPlayer.z);
          combatHud.setTarget({
            name: targetAlly.nickname,
            hp: targetAlly.hp,
            hpMax: targetAlly.hpMax,
            distanceM,
            isHostile: false,
            outOfRange: distanceM > reachOf('ALLY'),
          });
          targetRing.update(visuals.get(localTargetId)?.root.position ?? null, false, dtSeconds);
        } else {
          combatHud.setTarget(null);
          targetRing.update(null, true, dtSeconds);
          // Dead, gone from view, or back in the dark: the lock is released —
          // and the server told, so its soft target cannot outlive the
          // client's (2026-10-16; it used to keep the corpse's id).
          if (localTargetId) setLocalTarget('', false);
        }

        const localState = localPlayer.state as EntityStateName;
        const localAirborne = localJump.airborneAt(estimatedServerTimeNow());
        audioEngine.updateLocalFootsteps(
          dtSeconds, !localAirborne && (localState === 'MOVING' || localState === 'SPRINTING'), localState === 'SPRINTING', currentStratum,
        );

        /*
         * Everyone else's footsteps (audio.md Phase 1). This is the closest the
         * build gets to the pitch's "every fight you win tells three other
         * people exactly where you are" — though only the closest, not the
         * whole of it: `room.state.players` is @view()-gated at 10m lit / 4m
         * dark, so these are bodies you can already see. Hearing someone you
         * *cannot* see needs `noise.heard`, which noise-interest-management.md
         * specifies in full and which nothing implements. See audio.md §3.1.
         *
         * Monsters are included: they are bodies moving on the same floors, and
         * a Rattler shuffling behind you should be as audible as a delver.
         */
        const audibleBodies: FootstepEntity[] = [];
        for (const [id, other] of room.state.players.entries()) {
          if (id === localSessionId) continue; // the local player has its own tracker above
          audibleBodies.push({
            id, x: other.x, z: other.z, state: other.state,
            airborne: other.jumpStartedAt > 0 && isAirborne(estimatedServerTimeNow() - other.jumpStartedAt),
          });
        }
        for (const [id, monster] of room.state.monsters.entries()) {
          if (monster.state === 'DEAD') continue;
          audibleBodies.push({ id, x: monster.x, z: monster.z, state: monster.state });
        }
        for (const step of remoteFootsteps.update(dtSeconds, audibleBodies)) {
          audioEngine.playRemoteFootstep(
            { x: step.x, z: step.z }, currentStratum, step.isSprinting, step.gainMultiplier);
        }

        // "no clear goals or spawn logic" — there's always an actionable
        // "go here". Warden's Route amendment (2026-08-24): the arrow now
        // follows the fly-through route (goal-compass.ts explains why a
        // crow-flies bearing stopped working once the exit moved 12-18
        // rooms out), and only hands off to the portcullis itself once the
        // player is close enough for a straight line to mean something.
        let nearest: { x: number; z: number; dist: number } | null = null;
        for (const p of room.state.portcullises.values()) {
          const dist = Math.hypot(localPlayer.x - p.x, localPlayer.z - p.z);
          if (!nearest || dist < nearest.dist) nearest = { x: p.x, z: p.z, dist };
        }
        // MMO combat integration (2026-08-24) — cooldown shading and the cast
        // bar advance per frame off the estimated server clock, so a GCD
        // sweep is smooth instead of stepping once per state message.
        if (abilityState) {
          const serverNow = estimatedServerTimeNow();
          // Range tint first, so this frame's shading uses it (2026-10-16).
          actionBar.setTargetContext({ distanceM: enemyDistanceForBar(localPlayer) });
          actionBar.refreshCooldowns(serverNow);
          // The cast window comes from `ability.state`, not synced schema.
          const started = abilityState.castStartedAt ?? 0;
          const ends = abilityState.castEndsAt ?? 0;
          const casting = localPlayer.state === 'CASTING' && ends > started;
          actionBar.setCast(casting ? (serverNow - started) / (ends - started) : null);
        }

        const target = resolveCompassTarget(flyThroughWaypoints, nearest, localPlayer.x, localPlayer.z, EXIT_DIRECT_BEARING_M);
        if (target) {
          // Only once the compass is pointing at an actual gate. Explaining
          // extraction while it is still following route waypoints would be
          // pointing at a corridor and calling it the way out.
          if (target.source === 'gate') signalTutorial('run:extract-known');
          // "Exit" on a floor whose stairs go down is the same lie the
          // objective banner was telling — see `objectiveFor`.
          goalCompass.update(
            computeBearing(localPlayer.x, localPlayer.z, target.x, target.z, camera.viewYaw),
            target.distanceM,
            gateCompassLabel(),
          );
        } else {
          goalCompass.hide();
        }
      }
    }

    const renderNow = estimatedServerTimeNow();
    for (const [sessionId, interp] of remoteInterpolators.entries()) {
      const sample = interp.sample(renderNow);
      const visual = visuals.get(sessionId);
      if (!sample || !visual) continue;
      const isLocal = sessionId === localSessionId;
      // Jump (2026-10-16): the arc lifts the body only. The camera follows
      // `sample`, which stays on the floor, so a hop never bobs the view.
      const jumpY = isLocal
        ? localJump.offsetAt(renderNow)
        : remoteJumpOffset(renderNow, room?.state.players.get(sessionId)?.jumpStartedAt ?? 0);
      visual.setPose(sample.x, jumpY, sample.z, isLocal ? localRenderFacing(sample.facing) : sample.facing);
      if (isLocal && !flyThroughActive) {
        // Game Feel Polish Phase 4 — FOV sprint kick (the dash half went with the dodge roll).
        const localMoveState = room?.state.players.get(localSessionId)?.state as EntityStateName | undefined;
        const sprintOrDashActive = localMoveState === 'SPRINTING';
        dScene.followLocalPlayer(sample, camera.viewYaw, camera.viewPitch, camera.shoulderSide, dtSeconds, sprintOrDashActive);
      }
    }
    // Game Feel Polish Phase 3 — camera pose for this frame is now final; every spatialized sound played after this point uses it.
    audioEngine.setListenerPose({ x: dScene.camera.position.x, z: dScene.camera.position.z }, camera.viewYaw);

    combatHud.setVisible(!flyThroughActive);
    if (flyThroughActive) {
      flyThroughElapsedMs += dtSeconds * 1000;
      const t = flyThroughElapsedMs / flyThroughDurationMs;
      const { pos, lookAtPos } = samplePathPosition(flyThroughWaypoints, t);
      dScene.setFreeCameraPose(
        { x: pos.x, y: FLY_THROUGH_EYE_HEIGHT_M, z: pos.z },
        { x: lookAtPos.x, y: FLY_THROUGH_LOOKAT_HEIGHT_M, z: lookAtPos.z },
      );
      if (t >= 1) endFlyThrough();
    }

    // Combat-feel amendment (2026-08-21) — a brief animation-only freeze on
    // a landed hit involving the local player. Positions above are already
    // sampled from `renderNow` (a real clock estimate independent of this
    // scale), so movement stays perfectly server-truthful; only the mixer's
    // own playback timing (and VFX countdowns) briefly holds, which is what
    // actually sells the impact.
    const animDtSeconds = now < hitStopUntilMs ? dtSeconds * HIT_STOP_TIME_SCALE : dtSeconds;
    for (const visual of visuals.values()) visual.update(animDtSeconds);

    for (const [monsterId, interp] of monsterInterpolators.entries()) {
      const sample = interp.sample(renderNow);
      const visual = monsterVisuals.get(monsterId);
      if (!sample || !visual) continue;
      visual.setPose(sample.x, 0, sample.z, sample.facing);
    }
    for (const visual of monsterVisuals.values()) visual.update(animDtSeconds);

    // User report: "their models and textures block my view." The server-side
    // formation fix (sim/formation.ts) stops the party converging on the
    // player; this covers whatever still ends up in the shot — a squadmate
    // squeezed there by a corridor, a monster in melee, or the player's own
    // back when the boom collapses against a wall. Runs here because the
    // camera pose for this frame is final by now (followLocalPlayer above),
    // and after both pose loops so every root.position is this frame's.
    // User report: "their models and textures block my view." The server-side
    // formation fix (sim/formation.ts) stops the party converging on the
    // player; this covers whatever still ends up between the camera and the
    // player. Runs here because the camera pose for this frame is final by
    // now (followLocalPlayer above) and both pose loops have written every
    // root.position for this frame.
    //
    // The reference is the local player's own eye-distance, not a fixed
    // radius — see render/proximity-fade.ts for the measurements that ruled
    // the fixed radius out.
    const eye = dScene.camera.position;
    const localVisual = localSessionId ? visuals.get(localSessionId) : undefined;
    const playerEyeDistance = localVisual ? localVisual.root.position.distanceTo(eye) : 0;
    for (const [sessionId, visual] of visuals.entries()) {
      // The local player's own body is measured against the eye directly.
      // `cameraOcclusionAlpha` structurally cannot fade it — its lead is
      // always exactly 0 — and since MIN_CAMERA_DISTANCE was lowered so
      // geometry always wins the boom, your own model is now what ends up in
      // the lens when you back into a corner. See proximity-fade.ts.
      visual.setCameraFade(sessionId === localSessionId
        ? localPlayerFadeAlpha(dScene.cameraBoomLengthM)
        : cameraOcclusionAlpha(visual.root.position.distanceTo(eye), playerEyeDistance));
    }
    for (const [monsterId, visual] of monsterVisuals) {
      visual.setCameraFade(cameraOcclusionAlpha(visual.root.position.distanceTo(eye), playerEyeDistance));
      // Streamed is not seen (2026-10-16): monsters arrive from 16m so they
      // never pop in, but their rim and HP bar only show where lighting.md's
      // sight rule says the player could see them — see render/monster-reveal.ts.
      if (localVisual) {
        const pos = visual.root.position;
        const revealed = monsterRevealed(pos, localVisual.root.position, dScene.isLitAt(pos), monsterRevealedIds.has(monsterId));
        if (revealed !== monsterRevealedIds.has(monsterId)) {
          if (revealed) monsterRevealedIds.add(monsterId); else monsterRevealedIds.delete(monsterId);
        }
        visual.setTellsVisible(revealed);
      }
    }

    // Floating combat text. Projected here, after the camera pose is final, so
    // a number stays pinned to the body it belongs to for its whole life.
    damageNumbers.update(dtSeconds * 1000, {
      project: (x, y, z) => {
        projectVec.set(x, y, z).project(dScene.camera);
        if (projectVec.z > 1) return null; // behind the camera
        return {
          xPx: (projectVec.x * 0.5 + 0.5) * window.innerWidth,
          yPx: (-projectVec.y * 0.5 + 0.5) * window.innerHeight,
        };
      },
    });

    abilityFx.update(dtSeconds);

    dScene.render();

    // Lifted from here, not from a callback: "the world is ready" is a claim
    // about the picture, and this is the only place that knows one was drawn.
    if (entryVeil.isVisible) {
      if (layoutReady && localSessionId && visuals.has(localSessionId)) framesWithWorld += 1;
      if (framesWithWorld >= 2 || now >= veilDeadlineMs) entryVeil.dismiss();
    }

    rafHandle = requestAnimationFrame(frame);
  }
  rafHandle = requestAnimationFrame(frame);

  currentTeardown = () => {
    disposed = true;
    cancelAnimationFrame(rafHandle);
    input.stop();
    camera.stop();
    touch?.dispose();
    canvas.removeEventListener('mousedown', onCanvasMouseDown);
    canvas.removeEventListener('mouseup', onCanvasMouseUp);
    clockSync?.stop();
    unbindSettings.forEach((off) => off());
    audioEngine.dispose();
    room?.leave();
    for (const visual of visuals.values()) visual.dispose();
    visuals.clear();
    for (const visual of monsterVisuals.values()) visual.dispose();
    monsterVisuals.clear();
    abilityFx.dispose();
    targetRing.dispose();
    dScene.dispose();
    hud.dispose();
    lootPanel.dispose();
    lootToasts.dispose();
    interactPrompt.dispose();
    damageFlash.dispose();
    runEndOverlay.dispose();
    goalCompass.dispose();
  actionBar.dispose();
    flyThroughOverlay.dispose();
    mouseLookPrompt.dispose();
    entryVeil.dispose();
    levelUpBanner.dispose();
    downedOverlay.dispose();
    runMenu.dispose();
    abilityNotice.dispose();
    questBanner.dispose();
    tutorial.runner?.dispose();
    canvas.remove();
  };
  return currentTeardown;
}

export function unmountDungeonInstance(): void {
  currentTeardown?.();
  currentTeardown = null;
}
